import React, { useEffect, useState } from 'react';
import { bonuses, adminBonuses, clients, payoutBatches, PAYING_COMPANY_OPTIONS } from './api';
import PayoutBatches from './PayoutBatches';
import { OneTimeBonusModal, OneTimeBonusHistory, MyOneTimeBonuses } from './OneTimeBonuses';
import { CarryoverList, DelayedParts } from './PayoutSplit';
import { expectedLine } from './payoutSplit';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const fmtMoney = (v) => {
  const n = Number(v || 0);
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};
const fmtDate = (d) => {
  if (!d) return '—';
  // Plain YYYY-MM-DD strings get parsed as UTC by `new Date()`, which shifts
  // back a day in negative-offset timezones (the v3.3.2 effective-date bug).
  // Parse the parts manually so the date stays in local time.
  const m = String(d).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return new Date(+m[1], +m[2] - 1, +m[3]).toLocaleDateString();
  return new Date(d).toLocaleDateString();
};
// Audit timestamps are full ISO datetimes (real instants, not the bare
// YYYY-MM-DD that triggers the tz bug above), so plain Date parsing is fine.
const fmtDateTime = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleString();
};

const monthOptions = (() => {
  const opts = [];
  const now = new Date();
  for (let i = 0; i < 24; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    opts.push({
      year: d.getFullYear(),
      month: d.getMonth() + 1,
      label: d.toLocaleDateString('en-US', { year: 'numeric', month: 'long' }),
    });
  }
  return opts;
})();

async function openPrintUrl(url) {
  // Fetch with auth, then open the HTML in a new tab. <a href> can't carry the
  // Bearer token, so we have to fetch and inject as a blob URL.
  try {
    const token = localStorage.getItem('token');
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) {
      alert('Print failed (HTTP ' + res.status + ')');
      return;
    }
    const html = await res.text();
    const blob = new Blob([html], { type: 'text/html' });
    const blobUrl = window.URL.createObjectURL(blob);
    window.open(blobUrl, '_blank');
    // Don't revoke immediately — give the new tab time to load
    setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
  } catch (e) {
    alert('Print failed: ' + e.message);
  }
}

const STATUS_COLORS = {
  draft:      { bg: '#3a3a3a', color: '#ccc' },
  finalized:  { bg: '#185FA5', color: '#fff' },
  reviewed:   { bg: '#3B6D11', color: '#cfe9b6' },
  paid:       { bg: '#1a1a2e', color: '#888' },
  voided:     { bg: '#5a1a1a', color: '#e0a8a8' },
};

// ---------------------------------------------------------------------------
// Entry Audit History — reads the (previously unused) audit-log endpoints.
// Two-tier: default "audit" view (key decisions + meaningful edits; cosmetic
// app tidy-ups folded away) and a "Show full activity" toggle for the complete
// trail. Standalone so the Tier 2 conflict drawer embeds it with same filtering.
// ---------------------------------------------------------------------------

const AUDIT_CHURN_ACTIONS = new Set(['finalized', 'reviewed', 'returned_to_draft']);
const AUDIT_KEY_ACTIONS = new Set([
  'created', 'override_flag', 'override_flag_updated', 'override_dedup',
  'period_corrected', 'batch_closed', 'paid', 'batch_paid', 'voided',
  'manager_note_set', 'manager_note_cleared',
]);
// manager_note_* rows only ever reach an admin/manager (backend redacts them for
// staff in get_audit_log); rendered with a lock so it's clear they're private.
const AUDIT_OVERRIDE_ACTIONS = new Set(['override_flag', 'override_flag_updated', 'override_dedup']);
const AUDIT_MANAGER_NOTE_ACTIONS = new Set(['manager_note_set', 'manager_note_cleared']);
const AUDIT_EDIT_ACTIONS = new Set(['edited', 'admin_edit', 'staff_amount_edit']);
const AUDIT_NUMERIC_FIELDS = new Set(['bonus_value', 'premium_amount', 'policy_fee', 'bonus_amount_calculated', 'net_total_calculated']);

// Real human change, or app tidy-up (date timestamp reformat / customer_name file-number prefix)?
function auditEditIsMeaningful(field, oldV, newV) {
  const o = (oldV ?? '').toString(), n = (newV ?? '').toString();
  if (o === n) return false;
  const dnorm = s => { const m = s.match(/^\d{4}-\d{2}-\d{2}/); return m ? m[0] : s.trim(); };
  if (field === 'effective_date' || field === 'expiration_date') return dnorm(o) !== dnorm(n);
  if (field === 'customer_name') { const strip = s => s.replace(/^\d+\s+/, '').trim(); return strip(o) !== strip(n); }
  if (AUDIT_NUMERIC_FIELDS.has(field)) return parseFloat(o) !== parseFloat(n);
  return true;
}
function auditRowIsKey(r) {
  if (r.action === 'staff_amount_edit') return true;               // money — always
  if (r.action === 'edited' || r.action === 'admin_edit')
    return auditEditIsMeaningful(r.field_changed, r.old_value, r.new_value);
  if (AUDIT_KEY_ACTIONS.has(r.action)) return true;
  if (AUDIT_CHURN_ACTIONS.has(r.action)) return false;
  return true;                                                     // unknown action → never hide
}
function auditCleanOverrideDesc(s) {
  if (s == null) return s;
  const m = String(s).match(/^override_id=\d+\s*\((.*)\)\s*$/);    // "override_id=3 (red: …)" -> "red: …"
  return m ? m[1] : String(s);
}
function auditActionLabel(a) {
  return String(a).replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase());
}
// Default view: drop cosmetic edits, THEN fold same-second/same-action edit bursts.
function buildAuditItems(rows, showFull) {
  if (showFull) return rows.map(r => ({ type: 'row', row: r }));
  const key = rows.filter(auditRowIsKey);
  const items = [];
  for (let i = 0; i < key.length; ) {
    const r = key[i];
    if (AUDIT_EDIT_ACTIONS.has(r.action)) {
      const sec = String(r.created_at).slice(0, 19);
      const group = [r];
      let j = i + 1;
      while (j < key.length && key[j].action === r.action && String(key[j].created_at).slice(0, 19) === sec) { group.push(key[j]); j++; }
      if (group.length > 1) { items.push({ type: 'editGroup', rows: group }); i = j; continue; }
    }
    items.push({ type: 'row', row: r });
    i++;
  }
  return items;
}

function EntryAuditHistory({ entryId, admin, customerName, onClose, darkMode }) {
  const panelBg = darkMode ? '#242424' : '#fff';
  const borderColor = darkMode ? '#333' : '#e5e5e5';
  const textPrimary = darkMode ? '#f0f0f0' : '#1a1a1a';
  const textSecondary = darkMode ? '#aaa' : '#666';
  const accent = '#5BA3D9';
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  const [showFull, setShowFull] = useState(false);

  useEffect(() => {
    let alive = true;
    const apiClient = admin ? adminBonuses : bonuses;
    apiClient.auditLog(entryId)
      .then(res => { if (alive) setRows(res.data || []); })
      .catch(e => { if (alive) setError(e.response?.data?.detail || e.message || 'Failed to load history'); });
    return () => { alive = false; };
  }, [entryId, admin]);

  // Dismissal idiom matches EditBonusModal (✕ button) plus backdrop + Escape.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const keyCount = rows ? rows.filter(auditRowIsKey).length : 0;
  const items = rows ? buildAuditItems(rows, showFull) : [];

  const headerLine = (r) => (
    <div style={{ fontSize: 11, color: textSecondary }}>{fmtDateTime(r.created_at)} · {r.actor_name || `user #${r.actor_user_id}`}</div>
  );
  const fieldLine = (r) => (
    <div style={{ fontSize: 11, color: textSecondary }}>
      <span style={{ color: textPrimary }}>{r.field_changed}</span>: {r.old_value != null ? r.old_value : '—'} → {r.new_value != null ? r.new_value : '—'}
    </div>
  );
  const renderBody = (r) => {
    if (AUDIT_MANAGER_NOTE_ACTIONS.has(r.action)) return (
      <div style={{ fontSize: 12, color: textPrimary }}>
        🔒 {r.old_value ? `"${r.old_value}"` : '—'} → {r.new_value ? `"${r.new_value}"` : '(cleared)'}
      </div>
    );
    if (r.action === 'override_flag') return <div style={{ fontSize: 12, color: textPrimary }}>“{r.note}”</div>;
    if (r.action === 'override_flag_updated') return (<>
      <div style={{ fontSize: 12, color: textPrimary }}>“{auditCleanOverrideDesc(r.old_value)}” → “{auditCleanOverrideDesc(r.new_value)}”</div>
      {r.note && <div style={{ fontSize: 11, fontStyle: 'italic', color: textSecondary }}>{r.note}</div>}
    </>);
    if (r.action === 'override_dedup') return (<>
      {r.note && <div style={{ fontSize: 12, color: textPrimary }}>{r.note}</div>}
      {r.old_value && <div style={{ fontSize: 11, color: textSecondary }}>removed: “{auditCleanOverrideDesc(r.old_value)}”</div>}
    </>);
    if (AUDIT_EDIT_ACTIONS.has(r.action) && r.field_changed) return fieldLine(r);
    if (r.note) return <div style={{ fontSize: 11, fontStyle: 'italic', color: textSecondary }}>“{r.note}”</div>;
    return null;
  };

  return (
    <div onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: panelBg, borderRadius: 10, width: 640, maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 8px 32px rgba(0,0,0,0.5)' }}>
        <div style={{ padding: '14px 18px', borderBottom: `1px solid ${borderColor}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: textPrimary }}>Entry history</div>
            <div style={{ fontSize: 11, color: textSecondary, marginTop: 2 }}>{customerName || ''} · entry #{entryId}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: textSecondary, fontSize: 20, cursor: 'pointer' }}>✕</button>
        </div>

        {rows && rows.length > 0 && (
          <div style={{ padding: '8px 18px', borderBottom: `1px solid ${borderColor}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 11, color: textSecondary }}>
            <span>{showFull ? `${rows.length} total events` : `${keyCount} key event${keyCount === 1 ? '' : 's'}`}</span>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', color: textPrimary }}>
              <input type="checkbox" checked={showFull} onChange={e => setShowFull(e.target.checked)} />
              Show full activity
            </label>
          </div>
        )}

        <div style={{ padding: 18, overflowY: 'auto' }}>
          {error ? (
            <div style={{ color: '#e0a8a8', fontSize: 12 }}>{error}</div>
          ) : rows === null ? (
            <div style={{ color: textSecondary, fontSize: 12 }}>Loading…</div>
          ) : rows.length === 0 ? (
            <div style={{ color: textSecondary, fontSize: 12 }}>No history recorded.</div>
          ) : items.length === 0 ? (
            <div style={{ color: textSecondary, fontSize: 12 }}>No key events — toggle “Show full activity” for the complete trail.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {items.map((it, idx) => {
                if (it.type === 'editGroup') {
                  const first = it.rows[0];
                  return (
                    <div key={`g${idx}`} style={{ borderLeft: `2px solid ${borderColor}`, paddingLeft: 10, fontSize: 12, color: textPrimary }}>
                      {headerLine(first)}
                      <div style={{ fontWeight: 600 }}>{auditActionLabel(first.action)} · {it.rows.length} fields</div>
                      {it.rows.map(r => <React.Fragment key={r.id}>{fieldLine(r)}</React.Fragment>)}
                    </div>
                  );
                }
                const r = it.row;
                const isOverride = AUDIT_OVERRIDE_ACTIONS.has(r.action);
                return (
                  <div key={r.id} style={{ borderLeft: `2px solid ${isOverride ? accent : borderColor}`, paddingLeft: 10, fontSize: 12, color: textPrimary }}>
                    {headerLine(r)}
                    <div style={{ fontWeight: 600 }}>{auditActionLabel(r.action)}</div>
                    {renderBody(r)}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Add Bonus Modal — opens from a client folder
// ---------------------------------------------------------------------------

export function AddBonusModal({ client, sourceFolderPath, onClose, onSaved, darkMode }) {
  const bg = darkMode ? '#1a1a1a' : '#f5f5f5';
  const panelBg = darkMode ? '#242424' : '#fff';
  const borderColor = darkMode ? '#333' : '#e5e5e5';
  const textPrimary = darkMode ? '#f0f0f0' : '#1a1a1a';
  const textSecondary = darkMode ? '#aaa' : '#666';

  // Auto-fill the customer name from the folder name, stripping any leading
  // number prefix (e.g. "0123 TOMMY TRAN" -> "TOMMY TRAN"). Staff can edit.
  const initialCustomerName = (client.name || '').replace(/^\d+\s+/, '');

  const [form, setForm] = useState({
    customer_name: initialCustomerName,
    insured_name: '',
    customer_phone: client.phone1 || '',
    email: client.email || '',
    address: client.address_name || '',
    city: '',
    policy_number_raw: '',
    effective_date: new Date().toISOString().slice(0, 10),
    expiration_date: '',
    carrier: '',
    policy_type: '',
    premium_amount: '',
    bonus_type: 'percent',
    bonus_value: '',
    policy_fee: '',
    policy_fee_reason: '',
    notes: '',
    is_chargeback: false,
    chargeback_of_id: null,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [finalize, setFinalize] = useState(false);
  // v3.4: chargeback support
  const [eligibleOriginals, setEligibleOriginals] = useState([]);
  const [loadingEligible, setLoadingEligible] = useState(false);

  // Auto-set expiration to one year after effective date
  useEffect(() => {
    if (form.effective_date && !form.expiration_date) {
      const d = new Date(form.effective_date);
      d.setFullYear(d.getFullYear() + 1);
      setForm(f => ({ ...f, expiration_date: d.toISOString().slice(0, 10) }));
    }
    // eslint-disable-next-line
  }, [form.effective_date]);

  // v3.4: when chargeback toggle flips on, fetch prior bonuses on this client
  useEffect(() => {
    if (!form.is_chargeback) {
      setEligibleOriginals([]);
      return;
    }
    setLoadingEligible(true);
    fetch(`/api/bonuses/by-client/${client.id}/eligible-for-chargeback`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    })
      .then(r => r.json())
      .then(d => setEligibleOriginals(d.entries || []))
      .catch(() => setEligibleOriginals([]))
      .finally(() => setLoadingEligible(false));
  }, [form.is_chargeback, client.id]);

  const calcBonusAmount = () => {
    const premium = parseFloat(form.premium_amount) || 0;
    const value = parseFloat(form.bonus_value) || 0;
    if (form.bonus_type === 'percent') return (premium * value) / 100;
    return value;
  };
  const calcNetTotal = () => calcBonusAmount() + (parseFloat(form.policy_fee) || 0);

  const submit = async (doFinalize) => {
    setError('');
    setSaving(true);
    try {
      const payload = {
        ...form,
        client_id: client.id,
        source_folder_path: sourceFolderPath || null,
        premium_amount: parseFloat(form.premium_amount) || 0,
        bonus_value: parseFloat(form.bonus_value) || 0,
        policy_fee: form.is_chargeback ? 0 : (parseFloat(form.policy_fee) || 0),
        policy_fee_reason: form.is_chargeback ? null : form.policy_fee_reason,
      };
      // Empty optional strings -> null so Pydantic accepts cleanly
      ['insured_name','email','address','city','carrier',
       'policy_type','policy_fee_reason','notes'].forEach(k => {
        if (!payload[k]) payload[k] = null;
      });
      const res = await bonuses.create(payload);
      if (doFinalize) {
        await bonuses.finalize(res.data.id);
      }
      onSaved && onSaved(res.data);
      onClose();
    } catch (e) {
      let msg = e.response?.data?.detail;
      if (Array.isArray(msg)) {
        msg = msg.map(d => `${(d.loc || []).slice(1).join('.')}: ${d.msg}`).join('; ');
      }
      setError(msg || e.message || 'Failed to save');
    } finally {
      setSaving(false);
      setConfirmOpen(false);
    }
  };

  const fld = (label, key, opts = {}) => (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 11, color: textSecondary, marginBottom: 3 }}>
        {label} {opts.required && <span style={{ color: '#e74c3c' }}>*</span>}
        {opts.optional && <span style={{ color: textSecondary, fontWeight: 400 }}> (opt)</span>}
      </div>
      {opts.type === 'select' ? (
        <select value={form[key] || ''} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
          style={{ width: '100%', padding: '6px 8px', fontSize: 12, border: `1px solid ${borderColor}`, borderRadius: 5, background: bg, color: textPrimary, boxSizing: 'border-box' }}>
          {opts.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      ) : opts.type === 'textarea' ? (
        <textarea value={form[key] || ''} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
          rows={opts.rows || 2}
          style={{ width: '100%', padding: '6px 8px', fontSize: 12, border: `1px solid ${borderColor}`, borderRadius: 5, background: bg, color: textPrimary, boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }} />
      ) : (
        <input type={opts.type || 'text'} value={form[key] || ''}
          placeholder={opts.placeholder || ''}
          onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
          style={{ width: '100%', padding: '6px 8px', fontSize: 12, border: `1px solid ${borderColor}`, borderRadius: 5, background: bg, color: textPrimary, boxSizing: 'border-box' }} />
      )}
    </div>
  );

  return (
    <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 560, background: panelBg, zIndex: 850, display: 'flex', flexDirection: 'column', boxShadow: '-4px 0 20px rgba(0,0,0,0.4)', borderLeft: `1px solid ${borderColor}` }}>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>

        {/* Header */}
        <div style={{ padding: '14px 18px', borderBottom: `1px solid ${borderColor}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: textPrimary }}>Add Bonus</div>
            <div style={{ fontSize: 11, color: textSecondary, marginTop: 2 }}>
              {client.name} · #{client.id}
              {sourceFolderPath && <> · /{sourceFolderPath}</>}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: textSecondary, fontSize: 20, cursor: 'pointer' }}>✕</button>
        </div>

        {/* Body — two columns */}
        <div style={{ padding: 18, overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* Customer section */}
          <div>
            <div style={{ fontSize: 10, color: textSecondary, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10, fontWeight: 600 }}>Customer (auto-filled)</div>
            {fld('Customer name', 'customer_name', { required: true, placeholder: 'Auto-filled from folder; edit as needed' })}
            {fld('Insured name', 'insured_name', { optional: true, placeholder: 'If different from customer' })}
            {fld('Phone', 'customer_phone', { required: true })}
            {fld('Email', 'email', { optional: true })}
            {fld('Address', 'address', { optional: true })}
            {fld('City', 'city', { optional: true })}
          </div>

          {/* Policy + bonus section */}
          <div>
            <div style={{ fontSize: 10, color: textSecondary, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10, fontWeight: 600 }}>Policy &amp; bonus</div>
            {fld('Policy number', 'policy_number_raw', { required: true, placeholder: 'ABC-12345' })}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {fld('Effective', 'effective_date', { required: true, type: 'date' })}
              {fld('Expires', 'expiration_date', { required: true, type: 'date' })}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {fld('Carrier', 'carrier', { optional: true })}
              {fld('Type', 'policy_type', {
                optional: true,
                type: 'select',
                options: [
                  { value: '', label: '— select —' },
                  { value: 'Auto', label: 'Auto' },
                  { value: 'Home', label: 'Home' },
                  { value: 'Life', label: 'Life' },
                  { value: 'Commercial', label: 'Commercial' },
                  { value: 'Health', label: 'Health' },
                  { value: 'Renters', label: 'Renters' },
                  { value: 'Other', label: 'Other' },
                ]
              })}
            </div>
            {fld('Premium amount ($)', 'premium_amount', { required: true, type: 'number', placeholder: '1500.00' })}

            {/* v3.4: Chargeback toggle */}
            <div style={{ marginBottom: 10, padding: 10, border: `1px solid ${form.is_chargeback ? '#A32D2D' : borderColor}`, borderRadius: 5, background: form.is_chargeback ? (darkMode ? '#2a1414' : '#fdf2f2') : bg }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 12, color: textPrimary, fontWeight: 600 }}>
                <input type="checkbox" checked={form.is_chargeback}
                  onChange={e => setForm(f => ({ ...f, is_chargeback: e.target.checked, chargeback_of_id: null, policy_fee: '', policy_fee_reason: '' }))}
                  style={{ cursor: 'pointer' }} />
                <span style={{ color: form.is_chargeback ? '#A32D2D' : textPrimary }}>This is a chargeback (negative bonus)</span>
              </label>
              {form.is_chargeback && (
                <div style={{ marginTop: 10, fontSize: 11, color: textSecondary, lineHeight: 1.5 }}>
                  ⚠ Amount entered will be stored as <strong style={{ color: '#A32D2D' }}>negative</strong>. Policy fee is disabled on chargebacks. This subtracts from the period total.
                </div>
              )}
              {form.is_chargeback && (
                <div style={{ marginTop: 10 }}>
                  <div style={{ fontSize: 11, color: textSecondary, marginBottom: 3 }}>Chargeback of <span style={{ fontWeight: 400 }}>(opt — link to original bonus)</span></div>
                  {loadingEligible ? (
                    <div style={{ fontSize: 11, color: textSecondary, fontStyle: 'italic' }}>Loading prior bonuses on this client...</div>
                  ) : eligibleOriginals.length === 0 ? (
                    <div style={{ fontSize: 11, color: '#A32D2D', lineHeight: 1.5 }}>
                      No prior bonuses found on this client. If this charges back a bonus from an
                      already-paid month, don't enter it here — a pending chargeback belongs to no
                      period and won't show in that month's history. Ask a manager/admin to add it
                      as an adjustment on the open payout batch (Payout batches → + Add adjustment)
                      so it lands in the right period.
                    </div>
                  ) : (
                    <select value={form.chargeback_of_id || ''} onChange={e => setForm(f => ({ ...f, chargeback_of_id: e.target.value ? parseInt(e.target.value) : null }))}
                      style={{ width: '100%', padding: '6px 8px', fontSize: 12, border: `1px solid ${borderColor}`, borderRadius: 5, background: bg, color: textPrimary, boxSizing: 'border-box' }}>
                      <option value="">— not linked (free-form) —</option>
                      {eligibleOriginals.map(o => (
                        <option key={o.id} value={o.id}>
                          ${o.bonus_amount_calculated} — {o.policy_number_raw} — {o.staff_name} — {o.effective_date}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              )}
            </div>

            {/* Bonus calc */}
            <div style={{ background: bg, borderRadius: 5, padding: 10, marginBottom: 10 }}>
              <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
                {['percent', 'flat'].map(t => (
                  <button key={t} onClick={() => setForm(f => ({ ...f, bonus_type: t }))}
                    style={{ flex: 1, padding: 5, border: 'none', borderRadius: 4, background: form.bonus_type === t ? '#185FA5' : panelBg, color: form.bonus_type === t ? '#fff' : textPrimary, fontSize: 11, fontWeight: 600, cursor: 'pointer', textTransform: 'capitalize' }}>
                    {t === 'percent' ? 'Percent' : 'Flat $'}
                  </button>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="number" step="0.01" value={form.bonus_value}
                  onChange={e => setForm(f => ({ ...f, bonus_value: e.target.value }))}
                  placeholder={form.bonus_type === 'percent' ? '12.00' : '250.00'}
                  style={{ flex: 1, padding: '5px 8px', fontSize: 12, border: `1px solid ${borderColor}`, borderRadius: 4, background: panelBg, color: textPrimary, textAlign: 'right' }} />
                <span style={{ fontSize: 12, color: textSecondary }}>{form.bonus_type === 'percent' ? '% =' : 'flat ='}</span>
                <span style={{ fontSize: 14, fontWeight: 600, color: form.is_chargeback ? '#A32D2D' : textPrimary, minWidth: 80, textAlign: 'right' }}>
                  {form.is_chargeback ? '−' : ''}{fmtMoney(Math.abs(calcBonusAmount()))}
                </span>
              </div>
            </div>

            {!form.is_chargeback && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {fld('Policy fee', 'policy_fee', { optional: true, type: 'number', placeholder: '0.00' })}
                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 11, color: textSecondary, marginBottom: 3 }}>Net total</div>
                  <div style={{ padding: '6px 8px', fontSize: 14, fontWeight: 600, color: textPrimary, background: bg, border: `1px solid ${borderColor}`, borderRadius: 5 }}>
                    {fmtMoney(calcNetTotal())}
                  </div>
                </div>
              </div>
            )}
            {form.is_chargeback && (
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 11, color: textSecondary, marginBottom: 3 }}>Net total (chargeback)</div>
                <div style={{ padding: '6px 8px', fontSize: 14, fontWeight: 700, color: '#A32D2D', background: darkMode ? '#2a1414' : '#fdf2f2', border: `1px solid #A32D2D`, borderRadius: 5 }}>
                  −{fmtMoney(Math.abs(calcBonusAmount()))}
                </div>
              </div>
            )}
            {!form.is_chargeback && parseFloat(form.policy_fee) > 0 &&
              fld('Policy fee reason', 'policy_fee_reason', { required: true, placeholder: 'Endorsement processing' })}
            {fld('Notes', 'notes', { optional: true, type: 'textarea' })}
          </div>
        </div>

        {error && (
          <div style={{ padding: '8px 18px', color: '#e74c3c', fontSize: 12, background: '#fceaea', borderTop: `1px solid ${borderColor}` }}>
            {error}
          </div>
        )}

        {/* Footer */}
        <div style={{ padding: '12px 18px', borderTop: `1px solid ${borderColor}`, display: 'flex', justifyContent: 'flex-end', gap: 8, flexShrink: 0 }}>
          <button onClick={onClose}
            style={{ padding: '7px 16px', border: `1px solid ${borderColor}`, borderRadius: 5, background: panelBg, color: textSecondary, fontSize: 13, cursor: 'pointer' }}>
            Cancel
          </button>
          <button onClick={() => { setFinalize(false); submit(false); }} disabled={saving}
            style={{ padding: '7px 16px', border: `1px solid ${borderColor}`, borderRadius: 5, background: panelBg, color: textPrimary, fontSize: 13, cursor: 'pointer' }}>
            {saving && !finalize ? 'Saving...' : 'Save draft'}
          </button>
          <button onClick={() => setConfirmOpen(true)} disabled={saving}
            style={{ padding: '7px 16px', border: 'none', borderRadius: 5, background: '#185FA5', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            Save &amp; finalize
          </button>
        </div>
      </div>

      {/* Confirm finalize */}
      {confirmOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: panelBg, borderRadius: 8, padding: 22, width: 400, boxShadow: '0 8px 32px rgba(0,0,0,0.5)' }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: textPrimary, marginBottom: 8 }}>Confirm bonus entry</div>
            <div style={{ fontSize: 12, color: textSecondary, marginBottom: 14 }}>
              You're adding a bonus for <strong style={{ color: textPrimary }}>{form.customer_name}</strong> (Client #{client.id}).
              Net total: <strong style={{ color: form.is_chargeback ? '#A32D2D' : textPrimary }}>{form.is_chargeback ? '−' : ''}{fmtMoney(Math.abs(calcBonusAmount()))}</strong>.
              <br /><br />Once finalized, only the bonus amount can be edited by you. Admin can change other fields.
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => setConfirmOpen(false)}
                style={{ padding: '6px 14px', border: `1px solid ${borderColor}`, borderRadius: 5, background: panelBg, color: textSecondary, fontSize: 12, cursor: 'pointer' }}>Cancel</button>
              <button onClick={() => { setFinalize(true); submit(true); }} disabled={saving}
                style={{ padding: '6px 14px', border: 'none', borderRadius: 5, background: '#185FA5', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                {saving && finalize ? 'Saving...' : 'Confirm & finalize'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Edit Bonus Modal — opens from My Bonuses row dropdown
// ---------------------------------------------------------------------------

export function EditBonusModal({ bonusEntry, user, onClose, onSaved, darkMode }) {
  const bg = darkMode ? '#1a1a1a' : '#f5f5f5';
  const panelBg = darkMode ? '#242424' : '#fff';
  const borderColor = darkMode ? '#333' : '#e5e5e5';
  const textPrimary = darkMode ? '#f0f0f0' : '#1a1a1a';
  const textSecondary = darkMode ? '#aaa' : '#666';

  // Dismissal: Escape closes, same as the ✕/Cancel buttons (plain onClose,
  // no dirty-state confirm — matches the existing Cancel behavior so we don't
  // introduce a new data-loss path, just more ways to trigger the same close).
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const isDraft = bonusEntry.status === 'draft';
  const isInBatch = !!bonusEntry.payout_batch_id;
  const isStatusReturnable = bonusEntry.status === 'finalized' || bonusEntry.status === 'reviewed';
  const canReturnToDraft = isInBatch && isStatusReturnable &&
    (user?.role === 'admin' || user?.role === 'manager');
  const fmtForInput = (d) => d ? String(d).slice(0, 10) : '';

  const [form, setForm] = useState({
    customer_name: bonusEntry.customer_name || '',
    insured_name: bonusEntry.insured_name || '',
    customer_phone: bonusEntry.customer_phone || '',
    email: bonusEntry.email || '',
    address: bonusEntry.address || '',
    city: bonusEntry.city || '',
    policy_number_raw: bonusEntry.policy_number_raw,
    effective_date: fmtForInput(bonusEntry.effective_date),
    expiration_date: fmtForInput(bonusEntry.expiration_date),
    carrier: bonusEntry.carrier || '',
    policy_type: bonusEntry.policy_type || '',
    premium_amount: bonusEntry.premium_amount || '',
    bonus_type: bonusEntry.bonus_type,
    bonus_value: bonusEntry.bonus_value || '',
    policy_fee: bonusEntry.policy_fee || '',
    policy_fee_reason: bonusEntry.policy_fee_reason || '',
    notes: bonusEntry.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const calcBonusAmount = () => {
    const premium = parseFloat(form.premium_amount) || 0;
    const value = parseFloat(form.bonus_value) || 0;
    if (form.bonus_type === 'percent') return (premium * value) / 100;
    return value;
  };
  const calcNetTotal = () => calcBonusAmount() + (parseFloat(form.policy_fee) || 0);

  const submit = async () => {
    setError('');
    setSaving(true);
    try {
      let payload;
      if (isDraft) {
        payload = {
          ...form,
          premium_amount: parseFloat(form.premium_amount) || 0,
          bonus_value: parseFloat(form.bonus_value) || 0,
          policy_fee: parseFloat(form.policy_fee) || 0,
        };
        ['insured_name','email','address','city','carrier',
         'policy_type','policy_fee_reason','notes'].forEach(k => {
          if (!payload[k]) payload[k] = null;
        });
      } else {
        payload = {
          bonus_value: parseFloat(form.bonus_value) || 0,
          policy_fee: parseFloat(form.policy_fee) || 0,
          policy_fee_reason: form.policy_fee_reason || null,
        };
      }
      await bonuses.update(bonusEntry.id, payload);
      onSaved && onSaved();
      onClose();
    } catch (e) {
      let msg = e.response?.data?.detail;
      if (Array.isArray(msg)) {
        msg = msg.map(d => `${(d.loc || []).slice(1).join('.')}: ${d.msg}`).join('; ');
      }
      setError(msg || e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const fld = (label, key, opts = {}) => {
    const disabled = isInBatch || (!isDraft && !['bonus_value','policy_fee','policy_fee_reason'].includes(key));
    return (
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 11, color: textSecondary, marginBottom: 3 }}>
          {label} {opts.required && <span style={{ color: '#e74c3c' }}>*</span>}
          {opts.optional && <span style={{ color: textSecondary, fontWeight: 400 }}> (opt)</span>}
          {disabled && <span style={{ color: textSecondary, fontWeight: 400, fontStyle: 'italic' }}> · admin only</span>}
        </div>
        {opts.type === 'select' ? (
          <select value={form[key] || ''} disabled={disabled}
            onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
            style={{ width: '100%', padding: '6px 8px', fontSize: 12, border: `1px solid ${borderColor}`, borderRadius: 5, background: bg, color: textPrimary, boxSizing: 'border-box', opacity: disabled ? 0.5 : 1 }}>
            {opts.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        ) : opts.type === 'textarea' ? (
          <textarea value={form[key] || ''} disabled={disabled}
            onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
            rows={opts.rows || 2}
            style={{ width: '100%', padding: '6px 8px', fontSize: 12, border: `1px solid ${borderColor}`, borderRadius: 5, background: bg, color: textPrimary, boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit', opacity: disabled ? 0.5 : 1 }} />
        ) : (
          <input type={opts.type || 'text'} value={form[key] || ''} disabled={disabled}
            placeholder={opts.placeholder || ''}
            onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
            style={{ width: '100%', padding: '6px 8px', fontSize: 12, border: `1px solid ${borderColor}`, borderRadius: 5, background: bg, color: textPrimary, boxSizing: 'border-box', opacity: disabled ? 0.5 : 1 }} />
        )}
      </div>
    );
  };

  return (
    <div onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: panelBg, borderRadius: 10, width: 720, maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 8px 32px rgba(0,0,0,0.5)' }}>
        <div style={{ padding: '14px 18px', borderBottom: `1px solid ${borderColor}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: textPrimary }}>
              Edit bonus {!isDraft && <span style={{ fontSize: 12, fontWeight: 400, color: textSecondary }}>· {bonusEntry.status} · amount only</span>}
            </div>
            <div style={{ fontSize: 11, color: textSecondary, marginTop: 2 }}>
              {bonusEntry.customer_name || ''} · #{bonusEntry.client_id}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: textSecondary, fontSize: 20, cursor: 'pointer' }}>✕</button>
        </div>


        {isInBatch && (
          <div style={{ margin: '14px 18px 0', padding: '10px 12px',
            background: '#3a2a14', color: '#f5c97a',
            border: '1px solid #5a3a14', borderRadius: 5, fontSize: 12 }}>
            {bonusEntry.status === 'paid' ? (
              <>🔒 This bonus is in payout batch #{bonusEntry.payout_batch_id} and has been paid. It cannot be modified.</>
            ) : bonusEntry.status === 'voided' ? (
              <>🔒 This bonus is in payout batch #{bonusEntry.payout_batch_id} and has been voided. It cannot be modified.</>
            ) : (
              <>⚠ This bonus is in open payout batch #{bonusEntry.payout_batch_id}.
                {canReturnToDraft
                  ? ' Return it to draft to edit.'
                  : ' Only an admin or manager can return it to draft.'}
              </>
            )}
          </div>
        )}

        <div style={{ padding: 18, overflowY: 'auto', flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
          <div>
            <div style={{ fontSize: 10, color: textSecondary, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10, fontWeight: 600 }}>Customer</div>
            {fld('Customer name', 'customer_name', { required: true })}
            {fld('Insured name', 'insured_name', { optional: true })}
            {fld('Phone', 'customer_phone', { required: true })}
            {fld('Email', 'email', { optional: true })}
            {fld('Address', 'address', { optional: true })}
            {fld('City', 'city', { optional: true })}
          </div>

          <div>
            <div style={{ fontSize: 10, color: textSecondary, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10, fontWeight: 600 }}>Policy &amp; bonus</div>
            {fld('Policy number', 'policy_number_raw', { required: true })}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {fld('Effective', 'effective_date', { required: true, type: 'date' })}
              {fld('Expires', 'expiration_date', { required: true, type: 'date' })}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {fld('Carrier', 'carrier', { optional: true })}
              {fld('Type', 'policy_type', {
                optional: true, type: 'select',
                options: [
                  { value: '', label: '— select —' },
                  { value: 'Auto', label: 'Auto' },
                  { value: 'Home', label: 'Home' },
                  { value: 'Life', label: 'Life' },
                  { value: 'Commercial', label: 'Commercial' },
                  { value: 'Health', label: 'Health' },
                  { value: 'Renters', label: 'Renters' },
                  { value: 'Other', label: 'Other' },
                ]
              })}
            </div>
            {fld('Premium amount ($)', 'premium_amount', { required: true, type: 'number' })}

            <div style={{ background: bg, borderRadius: 5, padding: 10, marginBottom: 10 }}>
              <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
                {['percent', 'flat'].map(t => (
                  <button key={t} onClick={() => isDraft && !isInBatch && setForm(f => ({ ...f, bonus_type: t }))}
                    disabled={!isDraft || isInBatch}
                    style={{ flex: 1, padding: 5, border: 'none', borderRadius: 4, background: form.bonus_type === t ? '#185FA5' : panelBg, color: form.bonus_type === t ? '#fff' : textPrimary, fontSize: 11, fontWeight: 600, cursor: (isDraft && !isInBatch) ? 'pointer' : 'not-allowed', textTransform: 'capitalize', opacity: (isDraft && !isInBatch) ? 1 : 0.7 }}>
                    {t === 'percent' ? 'Percent' : 'Flat $'}
                  </button>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="number" step="0.01" value={form.bonus_value}
                  disabled={isInBatch}
                  onChange={e => setForm(f => ({ ...f, bonus_value: e.target.value }))}
                  style={{ flex: 1, padding: '5px 8px', fontSize: 12, border: `1px solid ${borderColor}`, borderRadius: 4, background: panelBg, color: textPrimary, textAlign: 'right', opacity: isInBatch ? 0.5 : 1 }} />
                <span style={{ fontSize: 12, color: textSecondary }}>{form.bonus_type === 'percent' ? '% =' : 'flat ='}</span>
                <span style={{ fontSize: 14, fontWeight: 600, color: textPrimary, minWidth: 80, textAlign: 'right' }}>{fmtMoney(calcBonusAmount())}</span>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {fld('Policy fee', 'policy_fee', { optional: true, type: 'number' })}
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 11, color: textSecondary, marginBottom: 3 }}>Net total</div>
                <div style={{ padding: '6px 8px', fontSize: 14, fontWeight: 600, color: textPrimary, background: bg, border: `1px solid ${borderColor}`, borderRadius: 5 }}>
                  {fmtMoney(calcNetTotal())}
                </div>
              </div>
            </div>
            {parseFloat(form.policy_fee) > 0 &&
              fld('Policy fee reason', 'policy_fee_reason', { required: true })}
            {fld('Notes', 'notes', { optional: true, type: 'textarea' })}
          </div>
        </div>

        {error && (
          <div style={{ padding: '8px 18px', color: '#e74c3c', fontSize: 12, background: '#fceaea', borderTop: `1px solid ${borderColor}` }}>
            {error}
          </div>
        )}

        <div style={{ padding: '12px 18px', borderTop: `1px solid ${borderColor}`, display: 'flex', justifyContent: 'flex-end', gap: 8, alignItems: 'center' }}>
          {canReturnToDraft && (
            <button onClick={async () => {
              if (!window.confirm('Return this bonus to draft and remove from the open payout batch?')) return;
              try {
                await payoutBatches.returnEntryToDraft(bonusEntry.id);
                onSaved && onSaved();
                onClose && onClose();
              } catch (e) {
                const detail = e.response?.data?.detail;
                const msg = Array.isArray(detail) ? detail.map(d => d.msg || JSON.stringify(d)).join('; ') : (detail || e.message);
                alert('Return to draft failed: ' + msg);
              }
            }} style={{ padding: '8px 14px', background: '#5a3a14', color: '#f5c97a',
               border: '1px solid #7a5024', borderRadius: 6, fontSize: 12, fontWeight: 600,
               cursor: 'pointer', marginRight: 'auto' }}>
              ⏎ Return to draft
            </button>
          )}
          <button onClick={onClose}
            style={{ padding: '7px 16px', border: `1px solid ${borderColor}`, borderRadius: 5, background: panelBg, color: textSecondary, fontSize: 13, cursor: 'pointer' }}>
            Cancel
          </button>
          <button onClick={submit} disabled={saving || isInBatch}
            style={{ padding: '7px 16px', border: 'none', borderRadius: 5, background: isInBatch ? '#444' : '#185FA5', color: '#fff', fontSize: 13, fontWeight: 600, cursor: isInBatch ? 'not-allowed' : 'pointer' }}>
            {saving ? 'Saving...' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// My Bonuses page
// ---------------------------------------------------------------------------

function MyBonuses({ user, darkMode, openClientFolder, bg, panelBg, borderColor, textPrimary, textSecondary, setTab, setSelectedBatchId }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [closing, setClosing] = useState(false);
  const [closeMsg, setCloseMsg] = useState('');
  const [editing, setEditing] = useState(null);
  const [historyFor, setHistoryFor] = useState(null);
  const [myBatches, setMyBatches] = useState([]);
  const [carries, setCarries] = useState([]);       // v5.49.0 waiting carry-overs (expected adjustment)

  const refresh = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      const res = await bonuses.listMine(params);
      setList(res.data);
    } catch (e) {
      setList([]);
    }
    try {
      setCarries((await bonuses.mineCarryovers()).data.rows || []);
    } catch (e) {
      setCarries([]);
    }
    setLoading(false);
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [statusFilter]);

  // Batches dropdown — refetch whenever the bonus list changes so a freshly
  // created batch (post Close-out) appears without a tab switch. Backend
  // list endpoints already enforce the staff-sees-own / admin-sees-all rule.
  useEffect(() => {
    const loadBatches = async () => {
      try {
        const [open, archived] = await Promise.all([
          payoutBatches.listOpen(),
          payoutBatches.listArchived(50, 0),
        ]);
        const merged = [...open.data, ...archived.data];
        merged.sort((a, b) => b.id - a.id);
        setMyBatches(merged);
      } catch (e) {
        // Non-fatal — dropdown just stays empty
        console.error('Failed to load batches for print dropdown', e);
      }
    };
    loadBatches();
  }, [list]);

  const closePeriod = async () => {
    if (!window.confirm('Close out all finalized bonuses into a new payout batch? They will be locked from edits until paid by an admin.')) return;
    setClosing(true);
    try {
      const res = await bonuses.closePeriod();
      const n = res.data.finalized_count;
      const co = res.data.carryover_count
        ? ` (with a carry-over of ${(Number(res.data.carryover_total) > 0 ? '+' : '') + fmtMoney(res.data.carryover_total)} from your last payout: ${fmtMoney(res.data.batch_total)})`
        : '';
      setCloseMsg(`Batch #${res.data.batch_id} created — ${n} bonus${n === 1 ? '' : 'es'} totaling ${fmtMoney(res.data.finalized_total)}${co} awaiting payment`);
      setTimeout(() => setCloseMsg(''), 6000);
      await refresh();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(d => d.msg || JSON.stringify(d)).join('; ') : (detail || e.message);
      alert('Close out failed: ' + msg);
    }
    setClosing(false);
  };

  const openBatch = (batchId) => {
    if (!setTab || !setSelectedBatchId) return;
    setSelectedBatchId(batchId);
    setTab('batches');
  };

  const onRowAction = async (b, action) => {
    try {
      if (action === 'edit') {
        setEditing(b);
      } else if (action === 'finalize') {
        if (!window.confirm('Finalize this bonus? Once finalized, only the bonus amount can be edited.')) return;
        await bonuses.finalize(b.id);
        await refresh();
      } else if (action === 'folder') {
        openClientFolder && openClientFolder(b.client_id, { tab: 'mine' });
      } else if (action === 'history') {
        setHistoryFor(b);
      } else if (action === 'print') {
        await openPrintUrl(bonuses.printSingleUrl(b.id));
      }
    } catch (e) {
      alert('Action failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  const totals = list.reduce((acc, b) => {
    acc[b.status] = acc[b.status] || { count: 0, total: 0 };
    acc[b.status].count += b.carryover_id ? 0 : 1;      // v5.49.0 a carry-over line is money, not a bonus
    acc[b.status].total += Number(b.net_total_calculated || 0);
    return acc;
  }, {});

  // Section buckets — order matters for render
  const draftRows = list.filter(b => b.status === 'draft');
  const readyToCloseRows = list.filter(b =>
    (b.status === 'finalized' || b.status === 'reviewed') &&
    !b.payout_batch_id &&
    !b.voided_at
  );
  const inBatchRows = list.filter(b => b.payout_batch_id && b.status !== 'paid');
  const historyRows = list.filter(b => b.status === 'paid' || b.status === 'voided');
  const readyToCloseCount = readyToCloseRows.length;

  return (
    <div style={{ padding: 20, overflowY: 'auto', height: '100%', boxSizing: 'border-box' }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 600, color: textPrimary }}>My bonuses</div>
          <div style={{ fontSize: 12, color: textSecondary, marginTop: 3 }}>
            {readyToCloseCount} ready to close
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {myBatches.length > 0 && (
            <select
              defaultValue=""
              onChange={async (e) => {
                const batchId = e.target.value;
                if (!batchId) return;
                await openPrintUrl(payoutBatches.printUrl(batchId));
                e.target.value = ''; // reset so the same option is re-selectable
              }}
              style={{
                padding: '8px 12px',
                background: panelBg,
                color: textPrimary,
                border: `1px solid ${borderColor}`,
                borderRadius: 6,
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
              }}>
              <option value="">🖨 Print batch...</option>
              {myBatches.map(b => (
                <option key={b.id} value={b.id}>
                  Batch #{b.id} · {b.status} · {b.entry_count} {b.entry_count === 1 ? 'entry' : 'entries'} · {fmtMoney(b.total_amount)}
                </option>
              ))}
            </select>
          )}
          <button onClick={() => {
            const now = new Date();
            openPrintUrl(bonuses.printYearlyUrl(now.getFullYear()));
          }}
            style={{ padding: '8px 14px', background: panelBg, color: textPrimary, border: `1px solid ${borderColor}`, borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            🖨 Print YTD
          </button>
          <button onClick={() => openPrintUrl(bonuses.printLastPeriodUrl())}
            style={{ padding: '8px 14px', background: panelBg, color: textPrimary, border: `1px solid ${borderColor}`, borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            🖨 Print last period
          </button>
          <button onClick={() => openPrintUrl(bonuses.printUnpaidUrl())}
            style={{ padding: '8px 14px', background: panelBg, color: textPrimary, border: `1px solid ${borderColor}`, borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            🖨 Print since last period
          </button>
          <button onClick={closePeriod} disabled={readyToCloseCount === 0 || closing}
            style={{ padding: '8px 18px', background: readyToCloseCount > 0 ? '#185FA5' : '#444', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: readyToCloseCount > 0 ? 'pointer' : 'not-allowed' }}>
            {closing ? 'Closing...' : 'Close out finalized'}
          </button>
        </div>
      </div>

      {closeMsg && (
        <div style={{ padding: '8px 12px', background: '#1a3a1a', color: '#a8d8a8', borderRadius: 5, fontSize: 12, marginBottom: 14 }}>
          ✓ {closeMsg}
        </div>
      )}

      {/* Stat cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10, marginBottom: 18 }}>
        {['draft', 'finalized', 'reviewed', 'paid', 'voided'].map(s => {
          const t = totals[s] || { count: 0, total: 0 };
          return (
            <div key={s} style={{ background: panelBg, border: `1px solid ${borderColor}`, borderRadius: 6, padding: 12 }}>
              <div style={{ fontSize: 11, color: textSecondary, textTransform: 'capitalize' }}>{s}</div>
              <div style={{ fontSize: 22, fontWeight: 600, color: textPrimary, marginTop: 3 }}>{t.count}</div>
              <div style={{ fontSize: 11, color: textSecondary }}>{fmtMoney(t.total)}</div>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center' }}>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          style={{ padding: '6px 10px', fontSize: 12, border: `1px solid ${borderColor}`, borderRadius: 5, background: panelBg, color: textPrimary }}>
          <option value="">All statuses</option>
          {['draft', 'finalized', 'reviewed', 'paid', 'voided'].map(s => (
            <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div style={{ border: `1px solid ${borderColor}`, borderRadius: 6, overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 90px 90px 90px 90px 80px 90px 130px', padding: '8px 12px', background: panelBg, fontSize: 10, color: textSecondary, fontWeight: 600, textTransform: 'uppercase', alignItems: 'center', gap: 6 }}>
          <span>Customer</span>
          <span>Policy</span>
          <span>Effective</span>
          <span style={{ textAlign: 'right' }}>Premium</span>
          <span style={{ textAlign: 'right' }}>Bonus</span>
          <span style={{ textAlign: 'right' }}>Net total</span>
          <span>Status</span>
          <span>Batch</span>
          <span>Action</span>
        </div>
        {loading ? (
          <div style={{ padding: 30, textAlign: 'center', color: textSecondary }}>Loading...</div>
        ) : list.length === 0 ? (
          <div style={{ padding: 30, textAlign: 'center', color: textSecondary }}>
            No bonuses yet. Add one from a client folder.
          </div>
        ) : (() => {
          const sectionHeaderBg = darkMode ? '#252525' : panelBg;
          const sections = [
            { key: 'drafts', title: 'Drafts', rows: draftRows, clickable: false },
            { key: 'ready', title: 'Ready to close', rows: readyToCloseRows, clickable: false },
            { key: 'inbatch', title: 'In payout batches', rows: inBatchRows, clickable: true },
            { key: 'history', title: 'Paid / Voided history', rows: historyRows, clickable: false },
          ];
          const renderRow = (b) => {
            // v4.6: batch adjustments render as dedicated line items (note +
            // signed amount, no policy/customer columns, no folder link) — never
            // through the normal row template, which would show a blank-policy
            // row. Add/edit/delete live in the batch detail view, not here.
            if (b.is_adjustment) {
              const net = Number(b.net_total_calculated || 0);
              return (
                <div key={b.id} data-testid={b.carryover_id ? `my-carry-line-${b.id}` : undefined}
                  style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 90px 90px 90px 90px 80px 90px 130px', padding: '10px 12px', borderTop: `1px solid ${borderColor}`, background: darkMode ? '#201d18' : '#fffdf6', fontSize: 12, alignItems: 'center', gap: 6 }}>
                  <span style={{ gridColumn: '1 / 6', color: textPrimary, display: 'flex', gap: 8, alignItems: 'baseline', minWidth: 0 }}>
                    <span style={{ fontSize: 11, color: '#f5c97a', fontWeight: 700, whiteSpace: 'nowrap' }}>{b.carryover_id ? '↪ Carry-over' : '⚖ Adjustment'}</span>
                    <span style={{ color: textSecondary, overflow: 'hidden', textOverflow: 'ellipsis' }} title={b.notes || ''}>{b.notes || '—'}</span>
                  </span>
                  <span style={{ gridColumn: '6 / 7', color: net < 0 ? '#e0a8a8' : '#a8d8a8', textAlign: 'right', fontWeight: 600 }}>{(net > 0 ? '+' : '') + fmtMoney(net)}</span>
                  <span style={{ gridColumn: '7 / 8' }}>
                    <span style={{ padding: '2px 8px', borderRadius: 3, fontSize: 10, fontWeight: 600, background: '#3a2a14', color: '#f5c97a', textTransform: 'uppercase' }}>adj</span>
                  </span>
                  <span style={{ gridColumn: '8 / 9' }}>
                    {b.payout_batch_id ? (
                      <span onClick={() => openBatch(b.payout_batch_id)}
                        style={{ padding: '2px 7px', borderRadius: 3, fontSize: 10, fontWeight: 600, background: '#3a2a14', color: '#f5c97a', cursor: 'pointer' }}>
                        #{b.payout_batch_id}
                      </span>
                    ) : <span style={{ color: textSecondary, fontSize: 11 }}>—</span>}
                  </span>
                  <span style={{ gridColumn: '9 / 10', color: textSecondary, fontSize: 11 }}>—</span>
                </div>
              );
            }
            const sc = STATUS_COLORS[b.status] || { bg: '#444', color: '#ccc' };
            const customerName = b.customer_name || '';
            const locked = !!b.payout_batch_id;
            return (
              <div key={b.id}
                style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 90px 90px 90px 90px 80px 90px 130px', padding: '10px 12px', borderTop: `1px solid ${borderColor}`, background: panelBg, fontSize: 12, alignItems: 'center', gap: 6 }}>
                <span style={{ color: textPrimary, minWidth: 0 }}>
                  <span onClick={() => openClientFolder && openClientFolder(b.client_id, { tab: 'mine' })}
                    style={{ color: '#5BA3D9', cursor: 'pointer' }}>{customerName || '—'}</span>
                  {(b.insured_name || b.address) && (
                    <div style={{ fontSize: 10, color: textSecondary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {b.insured_name ? `Insured: ${b.insured_name}` : ''}{b.insured_name && b.address ? ' · ' : ''}{b.address || ''}
                    </div>
                  )}
                  {b.notes && (
                    <div title={b.notes} style={{ fontSize: 10, color: textSecondary, fontStyle: 'italic', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>📝 {b.notes}</div>
                  )}
                  {/* v5.45.0: a batch paid in parts / with a part still to come — lines up with the bank */}
                  {b.payment_summary && ((b.payment_parts || []).length > 1 || (b.payment_parts || []).some(p => p.status === 'delayed')) && (
                    <div data-testid={`entry-payment-${b.id}`} title={b.payment_summary}
                      style={{ fontSize: 10, color: '#a8d8a8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>💵 {b.payment_summary}</div>
                  )}
                </span>
                <span style={{ color: textPrimary, fontFamily: 'monospace', fontSize: 11 }}>{b.policy_number_raw}</span>
                <span style={{ color: textSecondary }}>{fmtDate(b.effective_date)}</span>
                <span style={{ color: textPrimary, textAlign: 'right' }}>{fmtMoney(b.premium_amount)}</span>
                <span style={{ color: textPrimary, textAlign: 'right' }}>{fmtMoney(b.bonus_amount_calculated)}</span>
                <span style={{ color: textPrimary, textAlign: 'right', fontWeight: 600 }}>{fmtMoney(b.net_total_calculated)}</span>
                <span>
                  <span style={{ padding: '2px 8px', borderRadius: 3, fontSize: 10, fontWeight: 600, background: sc.bg, color: sc.color, textTransform: 'capitalize' }}>{b.status}</span>
                </span>
                <span>
                  {b.payout_batch_id ? (
                    <span onClick={() => openBatch(b.payout_batch_id)}
                      style={{ padding: '2px 7px', borderRadius: 3, fontSize: 10, fontWeight: 600, background: '#3a2a14', color: '#f5c97a', cursor: 'pointer' }}>
                      #{b.payout_batch_id}
                    </span>
                  ) : <span style={{ color: textSecondary, fontSize: 11 }}>—</span>}
                </span>
                <select onChange={e => { if (e.target.value) { onRowAction(b, e.target.value); e.target.value = ''; } }}
                  defaultValue=""
                  style={{ padding: '4px 6px', fontSize: 11, border: `1px solid ${borderColor}`, borderRadius: 4, background: bg, color: textPrimary }}>
                  <option value="">Choose…</option>
                  {locked ? (
                    <>
                      <option value="folder">Open client folder</option>
                      <option value="history">History</option>
                      <option value="print">Print</option>
                    </>
                  ) : (
                    <>
                      {b.status === 'draft' && <option value="edit">Edit</option>}
                      {b.status === 'draft' && <option value="finalize">Finalize</option>}
                      {b.status === 'finalized' && <option value="edit">Edit amount</option>}
                      {b.status !== 'draft' && b.status !== 'voided' && <option value="print">Print</option>}
                      <option value="folder">Open client folder</option>
                      <option value="history">History</option>
                    </>
                  )}
                </select>
              </div>
            );
          };
          return sections.flatMap(sec => {
            if (sec.rows.length === 0) return [];
            const clickHandler = sec.clickable && sec.rows[0]?.payout_batch_id
              ? () => openBatch(sec.rows[0].payout_batch_id)
              : undefined;
            return [
              <div key={`hdr-${sec.key}`}
                onClick={clickHandler}
                style={{ padding: '8px 12px', background: sectionHeaderBg, borderTop: `1px solid ${borderColor}`, fontSize: 11, fontWeight: 600, color: textSecondary, textTransform: 'uppercase', letterSpacing: '0.04em', cursor: clickHandler ? 'pointer' : 'default' }}>
                {sec.title} ({sec.rows.length})
              </div>,
              ...sec.rows.map(renderRow),
            ];
          });
        })()}
      </div>

      {/* v5.49.0 an earlier payout was more / less than owed: what the next close-out will add */}
      {carries.map(c => (
        <div key={c.id} data-testid={`my-carryover-${c.id}`}
          style={{ marginTop: 12, padding: '8px 12px', border: '1px solid #f5c97a', borderRadius: 6, background: panelBg, fontSize: 12, color: textPrimary }}>
          <span style={{ color: '#f5c97a', fontWeight: 700 }}>↪ </span>{expectedLine(c)}
        </div>
      ))}

      {/* v5.9.0: read-only, hidden when the producer has none */}
      <MyOneTimeBonuses panelBg={panelBg} borderColor={borderColor} textPrimary={textPrimary} textSecondary={textSecondary} />

      {editing && (
        <EditBonusModal
          bonusEntry={editing}
          user={user}
          darkMode={darkMode}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await refresh(); }}
        />
      )}

      {historyFor && (
        <EntryAuditHistory
          entryId={historyFor.id}
          admin={false}
          customerName={historyFor.customer_name}
          darkMode={darkMode}
          onClose={() => setHistoryFor(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Admin Dashboard
// ---------------------------------------------------------------------------

function AdminDashboard({ user, darkMode, onPickEmployee, bg, panelBg, borderColor, textPrimary, textSecondary }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // v5.9.0 one-time bonuses (admin only). reloadKey re-fetches the dashboard
  // and the history after a create or void.
  const isAdmin = user?.role === 'admin';
  const [showOneTime, setShowOneTime] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [toast, setToast] = useState('');

  useEffect(() => {
    setLoading(true);
    adminBonuses.dashboard({ period_year: year, period_month: month })
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [year, month, reloadKey]);

  const onOneTimeSaved = () => {
    setShowOneTime(false);
    setToast('One-time bonus saved');
    setTimeout(() => setToast(''), 4000);
    setReloadKey(k => k + 1);
  };

  const cell = (count, total) => (
    <span style={{ textAlign: 'right' }}>
      <span style={{ color: textPrimary }}>{count}</span>
      <span style={{ color: textSecondary }}> · {fmtMoney(total)}</span>
    </span>
  );

  return (
    <div style={{ padding: 20, overflowY: 'auto', height: '100%', boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 600, color: textPrimary }}>Bonus dashboard</div>
          <div style={{ fontSize: 12, color: textSecondary, marginTop: 3 }}>
            Click an employee row to review their bonuses
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <a href={adminBonuses.dashboardExportUrl(year, month)}
            target="_blank" rel="noreferrer"
            onClick={e => { /* xlsx export is server-side */ }}
            style={{ padding: '7px 14px', background: panelBg, color: textPrimary, border: `1px solid ${borderColor}`, borderRadius: 5, fontSize: 12, textDecoration: 'none' }}>
            Export xlsx
          </a>
          {isAdmin && (
            <button onClick={() => setShowOneTime(true)}
              style={{ padding: '7px 14px', background: '#185FA5', color: '#fff', border: 'none', borderRadius: 5, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              + One-time bonus
            </button>
          )}
        </div>
      </div>

      {toast && (
        <div style={{ padding: '8px 12px', background: '#1a3a1a', color: '#a8d8a8', borderRadius: 5, fontSize: 12, marginBottom: 14 }}>✓ {toast}</div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <select value={`${year}-${month}`}
          onChange={e => {
            const [y, m] = e.target.value.split('-').map(Number);
            setYear(y); setMonth(m);
          }}
          style={{ padding: '6px 10px', fontSize: 12, border: `1px solid ${borderColor}`, borderRadius: 5, background: panelBg, color: textPrimary }}>
          {monthOptions.map(m => (
            <option key={`${m.year}-${m.month}`} value={`${m.year}-${m.month}`}>{m.label}</option>
          ))}
        </select>
      </div>

      {/* Per-employee table */}
      <div style={{ border: `1px solid ${borderColor}`, borderRadius: 6, overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 1fr 1fr 1fr 1fr', padding: '10px 14px', background: panelBg, fontSize: 10, color: textSecondary, fontWeight: 600, textTransform: 'uppercase', gap: 8 }}>
          <span>Employee</span>
          <span style={{ textAlign: 'right' }}>Paid this period</span>
          <span style={{ textAlign: 'right' }}>Paid YTD</span>
          <span style={{ textAlign: 'right' }}>Awaiting review</span>
          <span style={{ textAlign: 'right' }}>Awaiting payout</span>
          <span style={{ textAlign: 'right' }}>In draft</span>
        </div>

        {loading ? (
          <div style={{ padding: 30, textAlign: 'center', color: textSecondary }}>Loading...</div>
        ) : !data || data.rows.length === 0 ? (
          <div style={{ padding: 30, textAlign: 'center', color: textSecondary }}>No employees found.</div>
        ) : (
          <>
            {data.rows.map(r => (
              <div key={r.user_id}
                onClick={() => onPickEmployee && onPickEmployee(r.user_id, r.employee_name, year, month)}
                style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 1fr 1fr 1fr 1fr', padding: '12px 14px', borderTop: `1px solid ${borderColor}`, background: panelBg, fontSize: 12, alignItems: 'center', cursor: 'pointer' }}>
                <span style={{ color: textPrimary, fontWeight: 500 }}>{r.employee_name}</span>
                {cell(r.paid_period_count, r.paid_period_total)}
                {cell(r.paid_ytd_count, r.paid_ytd_total)}
                {cell(r.awaiting_review_count, r.awaiting_review_total)}
                {cell(r.awaiting_payout_count, r.awaiting_payout_total)}
                {cell(r.draft_count, r.draft_total)}
              </div>
            ))}
            {/* Agency total */}
            <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 1fr 1fr 1fr 1fr', padding: '12px 14px', borderTop: `1px solid ${borderColor}`, background: bg, fontSize: 12, alignItems: 'center', fontWeight: 600 }}>
              <span style={{ color: textPrimary }}>{data.agency_total.employee_name}</span>
              {cell(data.agency_total.paid_period_count, data.agency_total.paid_period_total)}
              {cell(data.agency_total.paid_ytd_count, data.agency_total.paid_ytd_total)}
              {cell(data.agency_total.awaiting_review_count, data.agency_total.awaiting_review_total)}
              {cell(data.agency_total.awaiting_payout_count, data.agency_total.awaiting_payout_total)}
              {cell(data.agency_total.draft_count, data.agency_total.draft_total)}
            </div>
          </>
        )}
      </div>

      {/* Year-end exports */}
      <div style={{ marginTop: 24, padding: 16, border: `1px solid ${borderColor}`, borderRadius: 6, background: panelBg }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: textPrimary, marginBottom: 4 }}>Year-end statements</div>
        <div style={{ fontSize: 11, color: textSecondary, marginBottom: 12 }}>Per-employee year-end bonus statements. Paid status only.</div>
        <YearlyExports year={year} setYear={setYear} darkMode={darkMode} bg={bg} panelBg={panelBg} borderColor={borderColor} textPrimary={textPrimary} textSecondary={textSecondary} />
      </div>

      {/* v5.9.0 one-time bonus history (admin only) */}
      {isAdmin && (
        <div style={{ marginTop: 24, padding: 16, border: `1px solid ${borderColor}`, borderRadius: 6, background: panelBg }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: textPrimary, marginBottom: 4 }}>One-time bonuses</div>
          <div style={{ fontSize: 11, color: textSecondary, marginBottom: 12 }}>Paid outside payout batches. Corrections by void only — voided rows stay listed.</div>
          <OneTimeBonusHistory darkMode={darkMode} reloadKey={reloadKey} onChanged={() => setReloadKey(k => k + 1)}
            bg={bg} panelBg={panelBg} borderColor={borderColor} textPrimary={textPrimary} textSecondary={textSecondary} />
        </div>
      )}

      {/* v5.45.0 delayed parts of split batch payments (admin only) */}
      {isAdmin && (
        <div style={{ marginTop: 24, padding: 16, border: `1px solid ${borderColor}`, borderRadius: 6, background: panelBg }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: textPrimary, marginBottom: 4 }}>Delayed bonus parts</div>
          <div style={{ fontSize: 11, color: textSecondary, marginBottom: 12 }}>Parts of paid batches still to be paid. Mark a part paid on its batch.</div>
          <DelayedParts reloadKey={reloadKey} panelBg={panelBg} borderColor={borderColor} textPrimary={textPrimary} textSecondary={textSecondary} />
        </div>
      )}

      {/* v5.49.0 carry-overs waiting for an employee's next payout (admin only) */}
      {isAdmin && (
        <div style={{ marginTop: 24, padding: 16, border: `1px solid ${borderColor}`, borderRadius: 6, background: panelBg }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: textPrimary, marginBottom: 4 }}>Bonus carry-overs</div>
          <div style={{ fontSize: 11, color: textSecondary, marginBottom: 12 }}>Batches paid more or less than owed. The difference is added to the employee's next batch when they close out.</div>
          <CarryoverList reloadKey={reloadKey} panelBg={panelBg} borderColor={borderColor} textPrimary={textPrimary} textSecondary={textSecondary} />
        </div>
      )}

      {isAdmin && showOneTime && (
        <OneTimeBonusModal darkMode={darkMode} onClose={() => setShowOneTime(false)} onSaved={onOneTimeSaved} />
      )}
    </div>
  );
}

function YearlyExports({ darkMode, panelBg, borderColor, textPrimary, textSecondary }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [employeeId, setEmployeeId] = useState('');
  const [employees, setEmployees] = useState([]);
  const years = [];
  for (let y = now.getFullYear(); y >= now.getFullYear() - 5; y--) years.push(y);

  // v5.9.0: active bonus producers + anyone paid in the selected year
  // (inactive producers who were paid still get a 1099). Refetch per year and
  // drop the selection if that employee isn't in the new year's list.
  useEffect(() => {
    let cancelled = false;
    adminBonuses.yearly1099Producers(year)
      .then(r => {
        if (cancelled) return;
        const list = r.data || [];
        setEmployees(list);
        setEmployeeId(id => (id && !list.some(u => String(u.id) === String(id)) ? '' : id));
      })
      .catch(() => { if (!cancelled) { setEmployees([]); setEmployeeId(''); } });
    return () => { cancelled = true; };
  }, [year]);

  // v5.9.0: one 1099 statement per paying company.
  const printEmployeeYearly = (company) => {
    if (!employeeId) { alert('Select an employee'); return; }
    openPrintUrl(adminBonuses.employeeYearlyStatementUrl(employeeId, year, company));
  };

  const printEmployeeLastPeriod = () => {
    if (!employeeId) { alert('Select an employee'); return; }
    openPrintUrl(adminBonuses.employeeLastPeriodUrl(employeeId));
  };

  const printEmployeeUnpaid = () => {
    if (!employeeId) { alert('Select an employee'); return; }
    openPrintUrl(adminBonuses.employeeUnpaidUrl(employeeId));
  };

  const printEmployeeYearlyStaffView = () => {
    if (!employeeId) { alert('Select an employee'); return; }
    openPrintUrl(adminBonuses.employeeYearlyPrintUrl(employeeId, year));
  };

  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
      <select value={year} onChange={e => setYear(Number(e.target.value))}
        style={{ padding: '6px 10px', fontSize: 12, border: `1px solid ${borderColor}`, borderRadius: 5, background: panelBg, color: textPrimary }}>
        {years.map(y => <option key={y} value={y}>{y}</option>)}
      </select>
      <span style={{ fontSize: 11, color: textSecondary }}>Year-end statement producers</span>
      <select value={employeeId} onChange={e => setEmployeeId(e.target.value)}
        style={{ padding: '6px 10px', fontSize: 12, border: `1px solid ${borderColor}`, borderRadius: 5, background: panelBg, color: textPrimary, minWidth: 180 }}>
        <option value="">— select employee —</option>
        {employees.map(u => <option key={u.id} value={u.id}>{u.full_name}{u.is_active ? '' : ' (inactive)'}</option>)}
      </select>
      <button onClick={() => printEmployeeYearly('harry_dao')}
        style={{ padding: '7px 14px', background: panelBg, color: textPrimary, border: `1px solid ${borderColor}`, borderRadius: 5, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
        🖨 Year-end bonus statement — Harry Dao
      </button>
      <button onClick={() => printEmployeeYearly('solutions')}
        style={{ padding: '7px 14px', background: panelBg, color: textPrimary, border: `1px solid ${borderColor}`, borderRadius: 5, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
        🖨 Year-end bonus statement — Solutions
      </button>
      <button onClick={printEmployeeLastPeriod}
        style={{ padding: '7px 14px', background: panelBg, color: textPrimary, border: `1px solid ${borderColor}`, borderRadius: 5, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
        🖨 Print last period
      </button>
      <button onClick={printEmployeeUnpaid}
        style={{ padding: '7px 14px', background: panelBg, color: textPrimary, border: `1px solid ${borderColor}`, borderRadius: 5, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
        🖨 Print since last period
      </button>
      <button onClick={printEmployeeYearlyStaffView}
        style={{ padding: '7px 14px', background: panelBg, color: textPrimary, border: `1px solid ${borderColor}`, borderRadius: 5, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
        🖨 Print YTD (staff view)
      </button>
      <a href={adminBonuses.yearlyAllExportUrl(year)}
        style={{ padding: '7px 14px', background: '#185FA5', color: '#fff', border: 'none', borderRadius: 5, fontSize: 12, fontWeight: 600, textDecoration: 'none' }}>
        Download per-employee xlsx
      </a>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Bonus Review (employee-scoped)
// ---------------------------------------------------------------------------

function BonusReview({ initialEmployeeId, initialEmployeeName, initialYear, initialMonth, initialStatusFilter, initialFlaggedOnly, user, darkMode, onBack, openClientFolder, bg, panelBg, borderColor, textPrimary, textSecondary }) {
  const now = new Date();
  const [employees, setEmployees] = useState([]);
  const [employeeId, setEmployeeId] = useState(initialEmployeeId || null);
  const [year, setYear] = useState(initialYear || now.getFullYear());
  const [month, setMonth] = useState(initialMonth || now.getMonth() + 1);
  const [statusFilter, setStatusFilter] = useState(initialStatusFilter || '');
  const [flaggedOnly, setFlaggedOnly] = useState(initialFlaggedOnly || false);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [historyFor, setHistoryFor] = useState(null);
  const [drawerFor, setDrawerFor] = useState(null);
  const [overrideFor, setOverrideFor] = useState(null);
  // v4.15: chargeback/adjustment summary strip — fetched independently of the
  // status filter so applied chargebacks can't be filtered into invisibility.
  const [flagSummary, setFlagSummary] = useState(null);
  const [showFlagLines, setShowFlagLines] = useState(false);
  const [carries, setCarries] = useState([]);       // v5.49.0 this employee's waiting carry-overs

  // Load employees once
  useEffect(() => {
    fetch('/api/auth/users/visible', { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then(r => r.json())
      .then(d => setEmployees((d.users || []).filter(u => u.is_active)))
      .catch(() => setEmployees([]));
  }, []);

  const refresh = async () => {
    if (!employeeId) { setList([]); setFlagSummary(null); setCarries([]); return; }
    setLoading(true);
    try {
      const params = { employee_id: employeeId, period_year: year, period_month: month };
      if (statusFilter) params.status = statusFilter;
      if (flaggedOnly) params.flagged_only = true;
      const res = await adminBonuses.list(params);
      setList(res.data);
    } catch (e) {
      setList([]);
    }
    // v4.15: summary strip — no status/flagged params, by design.
    try {
      const s = await adminBonuses.periodSummary({ employee_id: employeeId, period_year: year, period_month: month });
      setFlagSummary(s.data);
    } catch (e) {
      setFlagSummary(null);
    }
    try {
      setCarries((await adminBonuses.carryovers({ employee_id: employeeId })).data.rows || []);
    } catch (e) {
      setCarries([]);
    }
    setLoading(false);
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [employeeId, year, month, statusFilter, flaggedOnly]);

  const doAction = async (b, action) => {
    try {
      if (action === 'history') { setHistoryFor(b); return; }
      if (action === 'conflict') { setDrawerFor(b); return; }
      if (action === 'review') await adminBonuses.review(b.id, {});
      else if (action === 'pay') {
        const today = new Date().toISOString().slice(0, 10);
        const method = window.prompt('Payment method? (check/zelle/venmo/cash/other)', 'check');
        if (!method) return;
        const ref = window.prompt('Payment reference (check #, Zelle confirmation, etc.)?', '');
        await adminBonuses.pay(b.id, { paid_at: today, payment_method: method, payment_reference: ref || null });
      } else if (action === 'return') {
        const note = window.prompt('Note for the employee (optional):', '');
        await adminBonuses.returnToDraft(b.id, { note: note || null });
      } else if (action === 'void') {
        const reason = window.prompt('Void reason (required):');
        if (!reason) return;
        await adminBonuses.void(b.id, { reason });
      } else if (action === 'override') {
        if (!b.flags || b.flags.length === 0) { alert('No flags to override'); return; }
        setOverrideFor({ entryId: b.id, flag: b.flags[0] });  // two-field modal handles save + refresh
        return;
      }
      await refresh();
    } catch (e) {
      alert('Action failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  const totalsLine = (() => {
    const count = list.length;
    const total = list.reduce((s, b) => s + Number(b.net_total_calculated || 0), 0);
    const flagged = list.filter(b => b.flags && b.flags.length > 0).length;
    return `${count} ${count === 1 ? 'entry' : 'entries'} · ${fmtMoney(total)} total${flagged > 0 ? ` · ${flagged} flagged` : ''}`;
  })();

  const negColor = darkMode ? '#e0a8a8' : '#A32D2D';
  // v4.15: the default (no explicit status filter) now returns the period's
  // paid batch history too. Section it: actionable review work on top, paid
  // history below a labeled divider, so 40 paid rows never bury 3 pending
  // ones. An explicit status filter keeps the flat single-status list.
  const paidRows = statusFilter ? [] : list.filter(b => b.status === 'paid');
  const activeRows = statusFilter ? list : list.filter(b => b.status !== 'paid');
  const PAID_DIVIDER = '__paid_divider__';
  const sectionedRows = paidRows.length > 0
    ? [...activeRows, PAID_DIVIDER, ...paidRows]
    : activeRows;

  return (
    <div style={{ padding: 20, overflowY: 'auto', height: '100%', boxSizing: 'border-box' }}>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 600, color: textPrimary }}>Bonus review</div>
          <div style={{ fontSize: 12, color: textSecondary, marginTop: 3 }}>One employee at a time</div>
        </div>
        {onBack && (
          <button onClick={onBack}
            style={{ padding: '7px 14px', background: panelBg, color: textSecondary, border: `1px solid ${borderColor}`, borderRadius: 5, fontSize: 12, cursor: 'pointer' }}>
            ← Back to dashboard
          </button>
        )}
      </div>

      {/* Picker bar */}
      <div style={{ background: panelBg, border: `1px solid ${borderColor}`, borderRadius: 6, padding: '12px 14px', marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <label style={{ fontSize: 11, color: textSecondary }}>Period</label>
        <select value={`${year}-${month}`}
          onChange={e => {
            const [y, m] = e.target.value.split('-').map(Number);
            setYear(y); setMonth(m);
          }}
          style={{ padding: '6px 10px', fontSize: 12, border: `1px solid ${borderColor}`, borderRadius: 5, background: bg, color: textPrimary }}>
          {monthOptions.map(m => (
            <option key={`${m.year}-${m.month}`} value={`${m.year}-${m.month}`}>{m.label}</option>
          ))}
        </select>
        <label style={{ fontSize: 11, color: textSecondary, marginLeft: 8 }}>Employee</label>
        <select value={employeeId || ''} onChange={e => setEmployeeId(Number(e.target.value) || null)}
          style={{ padding: '6px 10px', fontSize: 12, border: `1px solid ${borderColor}`, borderRadius: 5, background: bg, color: textPrimary, minWidth: 180 }}>
          <option value="">— select —</option>
          {employees.map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
        </select>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          style={{ padding: '6px 10px', fontSize: 12, border: `1px solid ${borderColor}`, borderRadius: 5, background: bg, color: textPrimary }}>
          {/* v4.15: the default now includes the period's paid batch history
              (pending review work still sections on top). */}
          <option value="">All for period (default)</option>
          <option value="draft">Draft</option>
          <option value="finalized">Finalized only</option>
          <option value="reviewed">Reviewed only</option>
          <option value="paid">Paid</option>
          <option value="voided">Voided</option>
        </select>
        <label style={{ fontSize: 12, color: textSecondary, display: 'flex', alignItems: 'center', gap: 6 }}>
          <input type="checkbox" checked={flaggedOnly} onChange={e => setFlaggedOnly(e.target.checked)} />
          Flagged only
        </label>
      </div>

      {!employeeId ? (
        <div style={{ padding: 40, textAlign: 'center', color: textSecondary, background: panelBg, borderRadius: 6, border: `1px solid ${borderColor}` }}>
          Select an employee above to view their bonuses for the selected period.
        </div>
      ) : (
        <>
          <div style={{ marginBottom: 10, fontSize: 12, color: textSecondary }}>
            {employees.find(u => u.id === employeeId)?.full_name} · {monthOptions.find(m => m.year === year && m.month === month)?.label} · {totalsLine}
          </div>
          {/* v4.15: chargeback/adjustment strip — always shown when the period
              (or the pending pool) has CB/ADJ lines, regardless of the status
              filter above, so this information cannot be filtered out of sight. */}
          {flagSummary && (flagSummary.count > 0 || flagSummary.pending_count > 0) && (
            <div style={{ marginBottom: 10, border: `1px solid ${darkMode ? '#5a2a2a' : '#e5b8b8'}`, background: darkMode ? '#2a1414' : '#fdf2f2', borderRadius: 6, padding: '10px 14px', fontSize: 12 }}>
              {flagSummary.count > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 700, color: negColor }}>
                    ⚠ {flagSummary.count} chargeback/adjustment line{flagSummary.count === 1 ? '' : 's'} in this period
                  </span>
                  <span style={{ color: textPrimary }}>
                    · net <strong style={{ color: Number(flagSummary.net_total) < 0 ? negColor : (darkMode ? '#a8d8a8' : '#2e7d32') }}>
                      {(Number(flagSummary.net_total) > 0 ? '+' : '') + fmtMoney(flagSummary.net_total)}
                    </strong>
                  </span>
                  <span onClick={() => setShowFlagLines(v => !v)} style={{ color: '#5BA3D9', cursor: 'pointer', fontWeight: 600 }}>
                    {showFlagLines ? 'Hide lines ▴' : 'Show lines ▸'}
                  </span>
                  <span style={{ fontSize: 10, color: textSecondary }}>(shown regardless of status filter)</span>
                </div>
              )}
              {/* v5.11.0: lines also include negative one-time bonus lines (source 'one_time',
                  CB badge, by the payment's paid month) — keyed by source + id. */}
              {showFlagLines && flagSummary.lines.map(l => (
                <div key={`${l.source || 'entry'}-${l.id}`} style={{ marginTop: 6, paddingLeft: 18, display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 3, background: negColor, color: darkMode ? '#1a0d0d' : '#fff' }}>{l.is_chargeback ? 'CB' : 'ADJ'}</span>
                  <span style={{ color: textPrimary }}>{l.customer_name}</span>
                  <span style={{ color: textSecondary, fontFamily: 'monospace', fontSize: 11 }}>{l.policy_number_raw}</span>
                  <span style={{ fontWeight: 600, color: Number(l.net_total_calculated) < 0 ? negColor : (darkMode ? '#a8d8a8' : '#2e7d32') }}>
                    {(Number(l.net_total_calculated) > 0 ? '+' : '') + fmtMoney(l.net_total_calculated)}
                  </span>
                  <span style={{ color: textSecondary, fontSize: 11 }}>
                    {l.status}{l.source === 'one_time' ? ` · one-time #${l.one_time_bonus_id}` : (l.payout_batch_id ? ` · batch #${l.payout_batch_id}` : '')}{l.paid_at ? ` · paid ${fmtDate(l.paid_at)}` : ''}
                  </span>
                  {l.notes && <span style={{ color: textSecondary, fontSize: 11, fontStyle: 'italic' }}>{l.notes}</span>}
                </div>
              ))}
              {flagSummary.pending_count > 0 && (
                <div style={{ marginTop: flagSummary.count > 0 ? 8 : 0, color: negColor, fontWeight: 600 }}>
                  ⏳ {flagSummary.pending_count} pending chargeback/adjustment in NO period yet (lands in the producer's next close-out) · net {fmtMoney(flagSummary.pending_net)}
                  {flagSummary.pending_lines.map(l => (
                    <div key={`${l.source || 'entry'}-${l.id}`} style={{ marginTop: 4, paddingLeft: 18, fontWeight: 400, color: textPrimary, fontSize: 11 }}>
                      #{l.id} · {l.customer_name} · {l.policy_number_raw} · <strong style={{ color: negColor }}>{fmtMoney(l.net_total_calculated)}</strong> · {l.status}{l.notes ? ` · ${l.notes}` : ''}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          <div style={{ border: `1px solid ${borderColor}`, borderRadius: 6, overflow: 'hidden' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '20px 1.3fr 1.1fr 90px 90px 110px 150px', padding: '10px 14px', background: panelBg, fontSize: 10, color: textSecondary, fontWeight: 600, textTransform: 'uppercase' }}>
              <span></span><span>Customer</span><span>Policy</span>
              <span style={{ textAlign: 'right' }}>Premium</span>
              <span style={{ textAlign: 'right' }}>Bonus</span>
              <span style={{ textAlign: 'right' }}>Net total</span>
              <span>Action</span>
            </div>
            {loading ? (
              <div style={{ padding: 30, textAlign: 'center', color: textSecondary }}>Loading...</div>
            ) : list.length === 0 ? (
              <div style={{ padding: 30, textAlign: 'center', color: textSecondary }}>No bonuses for this employee/period.</div>
            ) : sectionedRows.map(b => {
              // v4.15: divider between actionable rows and the period's paid
              // batch history (default view only — see sectionedRows above).
              if (b === PAID_DIVIDER) {
                return (
                  <div key={PAID_DIVIDER}
                    style={{ padding: '8px 14px', borderTop: `1px solid ${borderColor}`, background: panelBg, fontSize: 10, color: textSecondary, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Paid in this period — batch history ({paidRows.length})
                  </div>
                );
              }
              // v4.6: a batched adjustment can surface here when an admin filters
              // by the period of a batch the employee closed. Render it as a
              // read-only dedicated line item — never the normal row, which would
              // both show a blank-policy row AND fire openClientFolder(null) on
              // the NULL client_id. Edit/delete live only in batch detail.
              if (b.is_adjustment) {
                const net = Number(b.net_total_calculated || 0);
                return (
                  <div key={b.id}
                    style={{ display: 'grid', gridTemplateColumns: '20px 1.3fr 1.1fr 90px 90px 110px 150px', padding: '10px 14px', borderTop: `1px solid ${borderColor}`, background: darkMode ? '#201d18' : '#fffdf6', fontSize: 12, alignItems: 'center', gap: 8 }}>
                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#BA7517', display: 'inline-block' }} />
                    <span style={{ gridColumn: '2 / 6', color: textPrimary, display: 'flex', gap: 8, alignItems: 'baseline', minWidth: 0 }}>
                      <span style={{ fontSize: 11, color: '#f5c97a', fontWeight: 700, whiteSpace: 'nowrap' }}>{b.carryover_id ? '↪ Carry-over' : '⚖ Adjustment'}</span>
                      <span style={{ color: textSecondary, overflow: 'hidden', textOverflow: 'ellipsis' }} title={b.notes || ''}>{b.notes || '—'}</span>
                    </span>
                    <span style={{ gridColumn: '6 / 7', color: net < 0 ? '#e0a8a8' : '#a8d8a8', textAlign: 'right', fontWeight: 600 }}>{(net > 0 ? '+' : '') + fmtMoney(net)}</span>
                    <span style={{ gridColumn: '7 / 8', fontSize: 11, color: textSecondary }}>{b.payout_batch_id ? `in batch #${b.payout_batch_id}` : '—'}{b.status === 'paid' ? ' · paid' : ''}</span>
                  </div>
                );
              }
              const flag = b.flags && b.flags[0];
              const flagBg = flag?.flag_color === 'red' ? '#3a1414' : flag?.flag_color === 'yellow' ? '#3a2a14' : panelBg;
              const dotColor = flag?.flag_color === 'red' ? '#A32D2D' : flag?.flag_color === 'yellow' ? '#BA7517' : '#666';
              const customerName = b.customer_name || '';
              return (
                <React.Fragment key={b.id}>
                  <div style={{ display: 'grid', gridTemplateColumns: '20px 1.3fr 1.1fr 90px 90px 110px 150px', padding: '10px 14px', borderTop: `1px solid ${borderColor}`, background: flagBg, fontSize: 12, alignItems: 'center', gap: 8 }}>
                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: dotColor, display: 'inline-block' }} />
                    <span style={{ color: textPrimary, minWidth: 0 }}>
                      <span onClick={() => openClientFolder && openClientFolder(b.client_id, { tab: 'review', reviewContext: { employeeId, employeeName: employees.find(u => u.id === employeeId)?.full_name, year, month, statusFilter, flaggedOnly } })}
                        style={{ color: '#5BA3D9', cursor: 'pointer' }}>{customerName}</span>
                      {b.is_chargeback && (
                        <span style={{ marginLeft: 6, fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 3, background: negColor, color: darkMode ? '#1a0d0d' : '#fff', verticalAlign: 'middle' }}
                          title="Chargeback — negative bonus">CB</span>
                      )}
                      <div style={{ fontSize: 10, color: textSecondary }}>{b.policy_type || ''}{b.carrier ? ` · ${b.carrier}` : ''}</div>
                      {(b.insured_name || b.address) && (
                        <div style={{ fontSize: 10, color: textSecondary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {b.insured_name ? `Insured: ${b.insured_name}` : ''}{b.insured_name && b.address ? ' · ' : ''}{b.address || ''}
                        </div>
                      )}
                    </span>
                    <span style={{ color: textPrimary, fontFamily: 'monospace', fontSize: 11 }}>{b.policy_number_raw}<div style={{ fontSize: 10, color: textSecondary, fontFamily: 'inherit' }}>{fmtDate(b.effective_date)} – {fmtDate(b.expiration_date)}</div></span>
                    <span style={{ color: textPrimary, textAlign: 'right' }}>{fmtMoney(b.premium_amount)}</span>
                    <span style={{ color: textPrimary, textAlign: 'right' }}>{fmtMoney(b.bonus_amount_calculated)}</span>
                    <span style={{ color: (b.is_chargeback || Number(b.net_total_calculated) < 0) ? negColor : textPrimary, textAlign: 'right', fontWeight: 600 }}>{fmtMoney(b.net_total_calculated)}</span>
                    <select onChange={e => { if (e.target.value) { doAction(b, e.target.value); e.target.value = ''; } }}
                      defaultValue=""
                      style={{ padding: '4px 6px', fontSize: 11, border: `1px solid ${borderColor}`, borderRadius: 4, background: bg, color: textPrimary }}>
                      <option value="">Choose action…</option>
                      {/* v3.3.2: managers can review and override flags, but NOT on their own bonus rows.
                          Admin bypasses self-check. Pay/void remain admin-only. */}
                      {(() => {
                        const isOwnRow = b.staff_user_id === user?.id;
                        const isAdmin = user?.role === 'admin';
                        const isManager = user?.role === 'manager';
                        const canReview = isAdmin || (isManager && !isOwnRow);
                        const canOverride = isAdmin || (isManager && !isOwnRow);
                        const canPay = isAdmin;
                        const canVoid = isAdmin;
                        const canReturn = isAdmin || isManager;
                        return (
                          <>
                            {b.status === 'finalized' && canReview && <option value="review">Approve (mark reviewed)</option>}
                            {b.status === 'reviewed' && canPay && <option value="pay">Mark paid</option>}
                            {(b.status === 'finalized' || b.status === 'reviewed') && !b.payout_batch_id && canReturn && <option value="return">Return to draft</option>}
                            {flag && canOverride && <option value="override">Override flag</option>}
                            {b.status !== 'paid' && b.status !== 'voided' && canVoid && <option value="void">Void</option>}
                            <option value="history">History</option>
                            <option value="conflict">Conflict snapshot</option>
                          </>
                        );
                      })()}
                    </select>
                  </div>
                  {flag && (
                    <div style={{ background: flagBg, padding: '0 14px 10px 44px', fontSize: 11, color: flag.flag_color === 'red' ? '#e0a8a8' : '#e0c8a8', borderTop: 'none' }}>
                      <strong style={{ fontWeight: 500 }}>{flag.flag_color === 'red' ? 'Red flag — period overlap >30 days:' : 'Yellow flag — non-overlapping prior claim:'}</strong>{' '}
                      {flag.other_staff_name} · {fmtDate(flag.other_effective_date)} – {fmtDate(flag.other_expiration_date)} · {fmtMoney(flag.other_bonus_amount)} · status: {flag.other_status}
                      {flag.override_id && (
                        <span style={{ color: '#888', fontStyle: 'italic' }} title={flag.override_note || undefined}>
                          {' '}· overridden{flag.override_by_name ? ` by ${flag.override_by_name}` : ''}
                          {flag.override_note ? `: "${flag.override_note.length > 40 ? flag.override_note.slice(0, 40) + '…' : flag.override_note}"` : ''}
                        </span>
                      )}
                      {' '}
                      <span onClick={() => setDrawerFor(b)} style={{ color: '#5BA3D9', cursor: 'pointer', fontWeight: 600 }}>View conflict ▸</span>
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </>
      )}

      {/* v5.49.0 the employee's waiting carry-overs — what their next close-out will add */}
      {carries.map(c => (
        <div key={c.id} data-testid={`review-carryover-${c.id}`}
          style={{ marginTop: 12, padding: '8px 12px', border: '1px solid #f5c97a', borderRadius: 6, background: panelBg, fontSize: 12, color: textPrimary }}>
          <span style={{ color: '#f5c97a', fontWeight: 700 }}>↪ </span>{expectedLine(c).replace('your last payout', 'their last payout')}
        </div>
      ))}

      {historyFor && (
        <EntryAuditHistory
          entryId={historyFor.id}
          admin={true}
          customerName={historyFor.customer_name}
          darkMode={darkMode}
          onClose={() => setHistoryFor(null)}
        />
      )}

      {drawerFor && (
        <ConflictDrawer
          entry={drawerFor}
          openClientFolder={openClientFolder}
          onChanged={refresh}
          darkMode={darkMode}
          onClose={() => setDrawerFor(null)}
        />
      )}

      {overrideFor && (
        <OverrideFlagModal
          entryId={overrideFor.entryId}
          flag={overrideFor.flag}
          darkMode={darkMode}
          onSaved={refresh}
          onClose={() => setOverrideFor(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Conflict snapshot drawer (Tier 2, Phase 1.3) — right-side panel modeled on
// RenewalModal + backdrop/Escape (EntryAuditHistory convention). Composes:
//   This conflict (entry.flags[], no refetch) · Transaction history (Endpoint A)
//   · Conflict decision history (Endpoint B) · Full entry history (embedded
//   EntryAuditHistory). Phase 2 (manager-note) + Phase 3 (link-as-split) slot
//   into the marked placeholders without restructuring.
// ---------------------------------------------------------------------------

// Human-readable gap between the current entry and the conflicting prior.
function conflictGapPhrase(entryEff, otherEff) {
  const d = Math.round((new Date(entryEff) - new Date(otherEff)) / 86400000);
  if (Math.abs(d) < 20) return 'about the same time';
  const m = Math.max(1, Math.round(Math.abs(d) / 30.44));
  return `${m} month${m === 1 ? '' : 's'} ${d >= 0 ? 'earlier' : 'later'}`;
}

// The reason sentence — keyed off the SERVER-authoritative flag.flag_reason,
// not re-derived predicates. flag_color drives the dot; flag_reason the words.
function conflictReasonSentence(entry, flag) {
  const gap = conflictGapPhrase(entry.effective_date, flag.other_effective_date);
  const who = flag.other_staff_name, when = fmtDate(flag.other_effective_date);
  const amt = fmtMoney(flag.other_bonus_amount), st = flag.other_status;
  switch (flag.flag_reason) {
    case 'active_conflict':
      return `⚠ Active competing claim. ${who} claimed this customer on ${when} (${gap}) — status ${st}, ${amt}. A different producer has a live claim within the last year, so this overlaps and needs a decision.`;
    case 'renewal':
      return `Renewal pattern (same producer). ${who} also has this customer on ${when} (${gap}), ${amt}. Same producer, so this reads as a normal renewal — not a competing claim.`;
    case 'draft':
      return `Uncommitted prior. ${who} has a draft (not finalized) on this customer from ${when} (${gap}), ${amt}. Another producer started an entry but hasn't committed — not a hard conflict yet.`;
    case 'aged':
      return `Likely change of hands. ${who}'s prior claim was ${when} (${gap}) — over a year ago, status ${st}. Reads as a legitimate change of hands rather than a competing active claim.`;
    default:
      return `Prior claim by ${who} on ${when} (${gap}), ${amt}, status ${st}.`;  // fallback (e.g. backend not yet reloaded)
  }
}

function ConflictDrawer({ entry, onClose, openClientFolder, onChanged, darkMode }) {
  const bg = darkMode ? '#1a1a1a' : '#f5f5f5';
  const panelBg = darkMode ? '#242424' : '#fff';
  const borderColor = darkMode ? '#333' : '#e5e5e5';
  const textPrimary = darkMode ? '#f0f0f0' : '#1a1a1a';
  const textSecondary = darkMode ? '#aaa' : '#666';
  const accent = '#5BA3D9';

  const [history, setHistory] = useState(null);   // Endpoint A
  const [trail, setTrail] = useState(null);       // Endpoint B
  const [err, setErr] = useState('');
  const [showFullHistory, setShowFullHistory] = useState(false);

  // Fetch A + B ONCE on open (per entry); not per-render.
  useEffect(() => {
    let alive = true;
    setHistory(null); setTrail(null); setErr('');
    Promise.all([
      adminBonuses.clientPolicyHistory(entry.client_id, entry.policy_number_normalized),
      adminBonuses.conflictDetail(entry.id),
    ])
      .then(([h, t]) => { if (alive) { setHistory(h.data); setTrail(t.data); } })
      .catch(e => { if (alive) setErr(e.response?.data?.detail || e.message || 'Failed to load conflict detail'); });
    return () => { alive = false; };
  }, [entry.id, entry.client_id, entry.policy_number_normalized]);

  // Dismissal: backdrop + Escape + ✕ (EntryAuditHistory convention).
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const flags = entry.flags || [];
  const [overrideFor, setOverrideFor] = useState(null);

  const doOverride = (flag) => setOverrideFor(flag);   // opens the two-field modal
  const afterOverride = async () => {
    const t = await adminBonuses.conflictDetail(entry.id);  // refresh the decision trail
    setTrail(t.data);
    onChanged && onChanged();                               // parent re-fetches flags
  };

  const titleStyle = { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: textSecondary, marginBottom: 8 };
  const mutedStyle = { fontSize: 12, color: textSecondary };
  const btnStyle = { padding: '5px 12px', fontSize: 12, border: `1px solid ${borderColor}`, borderRadius: 5, background: bg, color: textPrimary, cursor: 'pointer', fontWeight: 600 };
  const tag = (b, c) => ({ marginLeft: 6, padding: '1px 6px', borderRadius: 3, fontSize: 9, fontWeight: 700, background: b, color: c });

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000 }} />
      <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 600, maxWidth: '92vw', background: panelBg, color: textPrimary, zIndex: 1000, borderLeft: `1px solid ${borderColor}`, boxShadow: '-4px 0 16px rgba(0,0,0,0.3)', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <div style={{ padding: '14px 18px', borderBottom: `1px solid ${borderColor}`, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>Conflict snapshot</div>
            <div style={{ fontSize: 12, color: textSecondary, marginTop: 3 }}>
              <span onClick={() => openClientFolder && openClientFolder(entry.client_id)} style={{ color: accent, cursor: 'pointer' }}>{entry.customer_name}</span>
              {' · '}#{entry.client_id} · {entry.policy_number_raw}
            </div>
            <div style={{ fontSize: 11, color: textSecondary, marginTop: 2 }}>
              {entry.staff_name} · {fmtMoney(entry.net_total_calculated)} · {fmtDate(entry.effective_date)}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: textSecondary, fontSize: 20, cursor: 'pointer' }}>✕</button>
        </div>

        <div style={{ padding: 18, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 18 }}>
          {err && <div style={{ color: '#e0a8a8', fontSize: 12 }}>{err}</div>}

          {/* This conflict — from entry.flags[], no refetch */}
          <section>
            <div style={titleStyle}>This conflict</div>
            {flags.length === 0 ? (
              <div style={mutedStyle}>No active flags on this entry.</div>
            ) : flags.map(flag => {
              const dot = flag.flag_color === 'red' ? '#A32D2D' : '#BA7517';
              return (
                <div key={flag.conflicting_entry_id} style={{ border: `1px solid ${borderColor}`, borderLeft: `3px solid ${dot}`, borderRadius: 6, padding: '10px 12px', marginBottom: 8 }}>
                  <div style={{ fontSize: 12.5, lineHeight: 1.5 }}>{conflictReasonSentence(entry, flag)}</div>
                  <div style={{ marginTop: 8, fontSize: 11, color: textSecondary }}>
                    {flag.override_id
                      ? <>Decision: overridden{flag.override_by_name ? ` by ${flag.override_by_name}` : ''} — “{flag.override_note}”</>
                      : <em>No decision recorded yet.</em>}
                  </div>
                  {/* manager_note: present only when the backend sent it (admin/manager
                      viewer). Frontend is defensive-only — it just renders what's there. */}
                  {flag.manager_note && (
                    <div style={{ marginTop: 8, fontSize: 11, color: textPrimary, background: bg, border: `1px solid ${borderColor}`, borderRadius: 5, padding: '6px 8px' }}>
                      🔒 <span style={{ color: textSecondary, fontWeight: 600 }}>Manager note:</span> “{flag.manager_note}”
                    </div>
                  )}
                  <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                    <button onClick={() => doOverride(flag)} style={btnStyle}>{flag.override_id ? 'Re-override…' : 'Override flag…'}</button>
                    {/* Phase 3: "Link as split" action renders here */}
                  </div>
                </div>
              );
            })}
          </section>

          {/* Transaction history — Endpoint A (fetched once) */}
          <section>
            <div style={titleStyle}>Transaction history</div>
            {history === null ? <div style={mutedStyle}>Loading…</div>
              : history.groups.length === 0 ? <div style={mutedStyle}>No bonus history for this client.</div>
              : history.groups.map(g => (
                <div key={g.policy_number_normalized} style={{ marginBottom: 10, border: `1px solid ${g.is_highlight ? '#BA7517' : borderColor}`, borderRadius: 6, overflow: 'hidden' }}>
                  <div style={{ padding: '6px 10px', background: g.is_highlight ? '#3a2a14' : bg, fontSize: 11, fontWeight: 600 }}>
                    {g.is_highlight ? '★ ' : ''}Policy {g.entries[0]?.policy_number_raw || g.policy_number_normalized}
                    <div style={{ fontWeight: 400, color: textSecondary, fontSize: 10 }}>
                      {g.customer_name || ''}{g.insured_name ? ` · ins: ${g.insured_name}` : ''}{g.address ? ` · ${g.address}` : ''}
                    </div>
                  </div>
                  {g.entries.map(e => {
                    const voided = e.status === 'voided';
                    return (
                      <div key={e.id} style={{ display: 'grid', gridTemplateColumns: '1fr 86px 78px 64px', gap: 6, padding: '5px 10px', borderTop: `1px solid ${borderColor}`, fontSize: 11, alignItems: 'center', opacity: voided ? 0.5 : 1 }}>
                        <span>{e.staff_name}
                          {voided && <span style={tag('#5a1a1a', '#e0a8a8')}>VOIDED</span>}
                          {e.is_chargeback && <span style={tag('#1a2e1a', '#a8d8a8')}>CB</span>}
                        </span>
                        <span style={{ textAlign: 'right', textDecoration: voided ? 'line-through' : 'none' }}>{fmtMoney(e.net_total_calculated)}</span>
                        <span style={{ color: textSecondary }}>{e.status}</span>
                        <span style={{ color: textSecondary, textAlign: 'right' }}>{e.period_year ? `${e.period_year}-${String(e.period_month).padStart(2, '0')}` : '—'}</span>
                      </div>
                    );
                  })}
                </div>
              ))}
          </section>

          {/* Conflict decision history — Endpoint B (fetched once) */}
          <section>
            <div style={titleStyle}>Conflict decision history</div>
            {trail === null ? <div style={mutedStyle}>Loading…</div>
              : trail.override_trail.length === 0 ? <div style={mutedStyle}>No override or period decisions recorded.</div>
              : trail.override_trail.map(r => (
                <div key={r.id} style={{ borderLeft: `2px solid ${accent}`, paddingLeft: 10, fontSize: 11, marginBottom: 6 }}>
                  <div style={{ color: textSecondary }}>{fmtDateTime(r.created_at)} · {r.actor_name || `user #${r.actor_user_id}`}</div>
                  <div style={{ fontWeight: 600 }}>{String(r.action).replace(/_/g, ' ')}</div>
                  {r.note && <div style={{ fontStyle: 'italic', color: textSecondary }}>“{r.note}”</div>}
                  {(r.old_value != null || r.new_value != null) && (
                    <div style={{ color: textSecondary }}>{r.old_value != null ? r.old_value : '—'} → {r.new_value != null ? r.new_value : '—'}</div>
                  )}
                </div>
              ))}
          </section>

          {/* Full entry history — embeds the Tier-1 EntryAuditHistory (its own fetch, opens above the drawer) */}
          <section>
            <button onClick={() => setShowFullHistory(true)} style={btnStyle}>Full entry history…</button>
          </section>
        </div>
      </div>

      {showFullHistory && (
        <EntryAuditHistory
          entryId={entry.id}
          admin={true}
          customerName={entry.customer_name}
          darkMode={darkMode}
          onClose={() => setShowFullHistory(false)}
        />
      )}

      {overrideFor && (
        <OverrideFlagModal
          entryId={entry.id}
          flag={overrideFor}
          darkMode={darkMode}
          onSaved={afterOverride}
          onClose={() => setOverrideFor(null)}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Override flag modal (Tier 2, Phase 2.3) — two fields: the collaborative
// shared note (all-visible) + the private manager note (🔒, admin/manager-only).
// Replaces the old single-field window.prompt. The manager-note gate is the
// BACKEND (find_flags / get_audit_log); this UI just renders/sends the field.
// ---------------------------------------------------------------------------

function OverrideFlagModal({ entryId, flag, onClose, onSaved, darkMode }) {
  const panelBg = darkMode ? '#242424' : '#fff';
  const bg = darkMode ? '#1a1a1a' : '#f5f5f5';
  const borderColor = darkMode ? '#333' : '#e5e5e5';
  const textPrimary = darkMode ? '#f0f0f0' : '#1a1a1a';
  const textSecondary = darkMode ? '#aaa' : '#666';
  const [note, setNote] = useState(flag.override_note || '');
  const [managerNote, setManagerNote] = useState(flag.manager_note || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const save = async () => {
    if (!note.trim()) { setError('Shared note is required.'); return; }
    setSaving(true); setError('');
    try {
      await adminBonuses.overrideFlag(entryId, {
        conflicting_entry_id: flag.conflicting_entry_id,
        flag_color: flag.flag_color,
        note: note.trim(),
        manager_note: managerNote.trim() || null,
      });
      if (onSaved) await onSaved();
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Override failed');
      setSaving(false);
    }
  };

  const inputStyle = { width: '100%', boxSizing: 'border-box', padding: '8px 10px', fontSize: 13, background: bg, color: textPrimary, border: `1px solid ${borderColor}`, borderRadius: 5, resize: 'vertical' };
  const labelStyle = { fontSize: 11, fontWeight: 600, color: textSecondary, marginBottom: 4, display: 'block' };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: panelBg, borderRadius: 10, width: 520, maxWidth: '92vw', boxShadow: '0 8px 32px rgba(0,0,0,0.5)' }}>
        <div style={{ padding: '14px 18px', borderBottom: `1px solid ${borderColor}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: textPrimary }}>
            {flag.override_id ? 'Re-override' : 'Override'} {flag.flag_color} flag
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: textSecondary, fontSize: 20, cursor: 'pointer' }}>✕</button>
        </div>
        <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={labelStyle}>Shared note — visible to all (required)</label>
            <textarea value={note} onChange={e => setNote(e.target.value)} rows={2} style={inputStyle}
              placeholder="Why this flag is OK — the producer can see this" />
          </div>
          <div>
            <label style={labelStyle}>🔒 Manager note — managers only (optional)</label>
            <textarea value={managerNote} onChange={e => setManagerNote(e.target.value)} rows={2} style={inputStyle}
              placeholder="Private context; never shown to the producer being adjudicated" />
          </div>
          {error && <div style={{ color: '#e0a8a8', fontSize: 12 }}>{error}</div>}
        </div>
        <div style={{ padding: '12px 18px', borderTop: `1px solid ${borderColor}`, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onClose} disabled={saving} style={{ padding: '7px 14px', fontSize: 12, border: `1px solid ${borderColor}`, borderRadius: 5, background: panelBg, color: textSecondary, cursor: 'pointer' }}>Cancel</button>
          <button onClick={save} disabled={saving || !note.trim()} style={{ padding: '7px 16px', fontSize: 12, border: 'none', borderRadius: 5, background: '#185FA5', color: '#fff', fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer' }}>{saving ? 'Saving…' : 'Save override'}</button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// v5.9.0 Bonus companies (admin only) — record which company paid each PAID
// payout batch. Changes save immediately (logged server-side, reversible);
// once set there is no blank option, so a company can't be cleared.
// ---------------------------------------------------------------------------

function BonusCompanies({ darkMode, bg, panelBg, borderColor, textPrimary, textSecondary }) {
  const now = new Date();
  const years = [];
  for (let y = now.getFullYear(); y >= now.getFullYear() - 5; y--) years.push(y);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [year, setYear] = useState('');            // '' = all years
  const [unassignedOnly, setUnassignedOnly] = useState(true);
  // Rows assigned in this session stay visible under "Unassigned only" until
  // the next reload, so a row doesn't vanish the moment you pick a company.
  const [justSet, setJustSet] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [msg, setMsg] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      // Fetch every paid batch for the year so the "N of M" count is right;
      // the Unassigned-only filter is applied client-side.
      const res = await adminBonuses.batchCompanies(year ? { year } : {});
      setRows(res.data || []);
      setJustSet({});
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError(typeof d === 'string' ? d : (e.message || 'Failed to load'));
      setRows([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [year]);

  const setCompany = async (row, value) => {
    if (!value || value === row.paying_company) return;
    setSavingId(row.id);
    try {
      const res = await adminBonuses.setBatchCompany(row.id, value);
      setRows(rs => rs.map(r => r.id === row.id
        ? { ...r, paying_company: res.data.paying_company, paying_company_name: res.data.paying_company_name }
        : r));
      setJustSet(js => ({ ...js, [row.id]: true }));
      setMsg(`Batch #${row.id} → ${res.data.paying_company_name}`);
      setTimeout(() => setMsg(''), 4000);
    } catch (e) {
      const d = e?.response?.data?.detail;
      alert('Could not set company: ' + (typeof d === 'string' ? d : (e.message || 'error')));
    }
    setSavingId(null);
  };

  const unassignedCount = rows.filter(r => !r.paying_company).length;
  const visible = unassignedOnly ? rows.filter(r => !r.paying_company || justSet[r.id]) : rows;
  const periodLabel = (y, m) => (y && m) ? new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : '—';
  const cols = '70px 1.3fr 90px 100px 70px 100px 70px 1fr 260px';
  const inputStyle = { padding: '6px 8px', fontSize: 12, border: `1px solid ${borderColor}`, borderRadius: 5, background: bg, color: textPrimary };

  return (
    <div style={{ padding: 20, overflowY: 'auto', height: '100%', boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14, gap: 12 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 600, color: textPrimary }}>Bonus companies</div>
          <div style={{ fontSize: 12, color: textSecondary, marginTop: 3 }}>
            Which company paid each payout batch. Changes save immediately and are logged.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <label style={{ fontSize: 12, color: textPrimary, display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
            <input type="checkbox" checked={unassignedOnly} onChange={e => { setUnassignedOnly(e.target.checked); setJustSet({}); }} />
            Unassigned only
          </label>
          <select value={year} onChange={e => setYear(e.target.value)} style={inputStyle}>
            <option value="">All years</option>
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>

      {!loading && !error && (
        <div style={{ padding: '8px 12px', marginBottom: 12, borderRadius: 5, fontSize: 13, fontWeight: 600,
          background: unassignedCount ? (darkMode ? '#3a2a14' : '#fff6e5') : (darkMode ? '#1a3a1a' : '#eaf6ea'),
          color: unassignedCount ? '#f5c97a' : (darkMode ? '#a8d8a8' : '#3B6D11') }}>
          {unassignedCount} of {rows.length} paid batches have no company{year ? ` (${year})` : ''}
        </div>
      )}
      {msg && (
        <div style={{ padding: '8px 12px', background: '#1a3a1a', color: '#a8d8a8', borderRadius: 5, fontSize: 12, marginBottom: 12 }}>✓ {msg}</div>
      )}

      <div style={{ border: `1px solid ${borderColor}`, borderRadius: 6, overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: cols, padding: '8px 12px', background: panelBg, fontSize: 10, color: textSecondary, fontWeight: 600, textTransform: 'uppercase', alignItems: 'center', gap: 6 }}>
          <span>Batch</span><span>Producer</span><span>Period</span><span>Paid</span>
          <span style={{ textAlign: 'right' }}>Entries</span><span style={{ textAlign: 'right' }}>Total</span>
          <span>Method</span><span>Reference</span><span>Paying company</span>
        </div>
        {loading ? (
          <div style={{ padding: 30, textAlign: 'center', color: textSecondary }}>Loading...</div>
        ) : error ? (
          <div style={{ padding: 30, textAlign: 'center', color: '#e0a8a8' }}>{error}</div>
        ) : visible.length === 0 ? (
          <div style={{ padding: 30, textAlign: 'center', color: textSecondary }}>
            {unassignedOnly ? 'Every paid batch has a company.' : 'No paid batches.'}
          </div>
        ) : visible.map(r => (
          <div key={r.id} style={{ display: 'grid', gridTemplateColumns: cols, padding: '8px 12px', borderTop: `1px solid ${borderColor}`, fontSize: 12, color: textPrimary, alignItems: 'center', gap: 6 }}>
            <span style={{ fontWeight: 700 }}>#{r.id}</span>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }} title={r.producer_name || ''}>{r.producer_name || '—'}</span>
            <span>{periodLabel(r.period_year, r.period_month)}</span>
            <span>{fmtDate(r.paid_at)}</span>
            <span style={{ textAlign: 'right' }}>{r.entry_count}</span>
            <span style={{ textAlign: 'right', fontWeight: 600 }}>{fmtMoney(r.total_amount)}</span>
            <span>{r.payment_method || '—'}</span>
            <span style={{ color: textSecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.payment_reference || ''}>{r.payment_reference || '—'}</span>
            {(r.parts || []).length > 1 ? (
              /* v5.45.0: a batch paid in several parts is read-only here — change it with Edit split on the batch */
              <span data-testid={`bc-parts-${r.id}`} style={{ fontSize: 11 }}>
                {(r.parts || []).map(p => (
                  <div key={p.id}>{fmtMoney(p.amount)} — {p.short_name}{p.status === 'delayed' ? ' (pending)' : ''}</div>
                ))}
                <div style={{ color: textSecondary, fontSize: 10 }}>Split — use Edit split on batch #{r.id}</div>
              </span>
            ) : (
            <span>
              <select value={r.paying_company || ''} disabled={savingId === r.id}
                onChange={e => setCompany(r, e.target.value)}
                style={{ ...inputStyle, width: '100%', borderColor: r.paying_company ? borderColor : '#f5c97a' }}>
                {!r.paying_company && <option value="" disabled>{r.paying_company_name}</option>}
                {PAYING_COMPANY_OPTIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main exported component — full-screen overlay with tabs
// ---------------------------------------------------------------------------

export default function Bonuses({ user, darkMode, onClose, openClientFolder, initialContext }) {
  const isAdminOrManager = user?.role === 'admin' || user?.role === 'manager';
  // v3.3.2: tab visibility decoupled from role. receives_bonuses controls
  // the personal sheet ("My bonuses"); admin/manager get Dashboard + Review.
  // Default landing tab: personal sheet if you have one, else Dashboard.
  const showMyBonuses = !!user?.receives_bonuses;
  // initialContext (from a "← Back to Bonuses" deep-link round trip) restores
  // the sub-tab and, for review, the employee/period/filter the user had set.
  const [tab, setTab] = useState(initialContext?.tab ?? (showMyBonuses ? 'mine' : 'dashboard'));
  const [reviewContext, setReviewContext] = useState(initialContext?.reviewContext ?? null); // { employeeId, employeeName, year, month, statusFilter, flaggedOnly }
  const [selectedBatchId, setSelectedBatchId] = useState(null);

  const bg = darkMode ? '#1a1a1a' : '#f5f5f5';
  const panelBg = darkMode ? '#242424' : '#fff';
  const borderColor = darkMode ? '#333' : '#e5e5e5';
  const textPrimary = darkMode ? '#f0f0f0' : '#1a1a1a';
  const textSecondary = darkMode ? '#aaa' : '#666';

  // When admin clicks an employee row in dashboard, jump to review pre-filtered
  const onPickEmployee = (employeeId, employeeName, year, month) => {
    setReviewContext({ employeeId, employeeName, year, month });
    setTab('review');
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: bg, color: textPrimary, zIndex: 900, display: 'flex', flexDirection: 'column' }}>
      {/* Top bar */}
      <div style={{ background: panelBg, borderBottom: `1px solid ${borderColor}`, padding: '0 18px', display: 'flex', alignItems: 'center', height: 50, gap: 12 }}>
        <span style={{ fontSize: 16, fontWeight: 700, color: '#185FA5' }}>💵 Bonuses</span>
        <div style={{ display: 'flex', borderRadius: 5, overflow: 'hidden', border: `1px solid ${borderColor}`, marginLeft: 8 }}>
          {showMyBonuses && (
            <button onClick={() => setTab('mine')}
              style={{ padding: '7px 16px', border: 'none', background: tab === 'mine' ? '#185FA5' : panelBg, color: tab === 'mine' ? '#fff' : textPrimary, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              My bonuses
            </button>
          )}
          {isAdminOrManager && (
            <>
              <button onClick={() => setTab('dashboard')}
                style={{ padding: '7px 16px', border: 'none', background: tab === 'dashboard' ? '#185FA5' : panelBg, color: tab === 'dashboard' ? '#fff' : textPrimary, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                Dashboard
              </button>
              <button onClick={() => { setReviewContext(null); setTab('review'); }}
                style={{ padding: '7px 16px', border: 'none', background: tab === 'review' ? '#185FA5' : panelBg, color: tab === 'review' ? '#fff' : textPrimary, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                Review
              </button>
            </>
          )}
          {(user?.role === 'admin' || user?.role === 'manager' || user?.receives_bonuses) && (
            <button onClick={() => setTab('batches')}
              style={{ padding: '7px 16px', border: 'none',
                background: tab === 'batches' ? '#185FA5' : panelBg,
                color: tab === 'batches' ? '#fff' : textPrimary,
                fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              Payout batches
            </button>
          )}
          {user?.role === 'admin' && (
            <button onClick={() => setTab('companies')}
              style={{ padding: '7px 16px', border: 'none',
                background: tab === 'companies' ? '#185FA5' : panelBg,
                color: tab === 'companies' ? '#fff' : textPrimary,
                fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              Bonus companies
            </button>
          )}
        </div>
        <span style={{ flex: 1 }} />
        <button onClick={onClose}
          style={{ padding: '7px 14px', background: panelBg, color: textPrimary, border: `1px solid ${borderColor}`, borderRadius: 5, fontSize: 12, cursor: 'pointer' }}>
          ✕ Close
        </button>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflow: 'hidden', minHeight: 0 }}>
        {tab === 'mine' && (
          <MyBonuses user={user} darkMode={darkMode} openClientFolder={openClientFolder}
            setTab={setTab} setSelectedBatchId={setSelectedBatchId}
            bg={bg} panelBg={panelBg} borderColor={borderColor} textPrimary={textPrimary} textSecondary={textSecondary} />
        )}
        {tab === 'dashboard' && isAdminOrManager && (
          <AdminDashboard user={user} darkMode={darkMode} onPickEmployee={onPickEmployee}
            bg={bg} panelBg={panelBg} borderColor={borderColor} textPrimary={textPrimary} textSecondary={textSecondary} />
        )}
        {tab === 'review' && isAdminOrManager && (
          <BonusReview
            initialEmployeeId={reviewContext?.employeeId}
            initialEmployeeName={reviewContext?.employeeName}
            initialYear={reviewContext?.year}
            initialMonth={reviewContext?.month}
            initialStatusFilter={reviewContext?.statusFilter}
            initialFlaggedOnly={reviewContext?.flaggedOnly}
            user={user} darkMode={darkMode}
            onBack={reviewContext ? () => { setReviewContext(null); setTab('dashboard'); } : null}
            openClientFolder={openClientFolder}
            bg={bg} panelBg={panelBg} borderColor={borderColor} textPrimary={textPrimary} textSecondary={textSecondary} />
        )}
        {tab === 'batches' && (user?.role === 'admin' || user?.role === 'manager' || user?.receives_bonuses) && (
          <PayoutBatches user={user} darkMode={darkMode}
            selectedBatchId={selectedBatchId} setSelectedBatchId={setSelectedBatchId}
            bg={bg} panelBg={panelBg} borderColor={borderColor}
            textPrimary={textPrimary} textSecondary={textSecondary} />
        )}
        {tab === 'companies' && user?.role === 'admin' && (
          <BonusCompanies darkMode={darkMode}
            bg={bg} panelBg={panelBg} borderColor={borderColor}
            textPrimary={textPrimary} textSecondary={textSecondary} />
        )}
      </div>
    </div>
  );
}
